import { Component, DestroyRef, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, map, skip, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  referencedSheetKeysFromSchema,
  recomputeReferencedSheets,
  buildSocieColumnContext,
} from '../../../services/mbrs/cross-sheet-recompute.util';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { MISMATCH_CORE } from '../../../services/mbrs/equality-message';
import { differsAtCentPrecision } from '../../../services/mbrs/validation-field-key.util';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { NegativeLabelPipe } from '../../../pipes/negative-label.pipe';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
  STATE_INHERITANCE_DEBOUNCE_MS,
  STATE_INHERITANCE_FAST_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  isRowVisible,
  isCollapsibleHeader as isCollapsibleHeaderRule,
  headerLevelClass,
} from '../../../utils/section-visibility.util';
import { SkeletonModule } from 'primeng/skeleton';
import { isMandatoryForValidation } from '../../../utils/required-field.util';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import {
  pickRole15SchemaFile,
} from '../../../services/mbrs/validators/role15-statement-of-comprehensive-income.rules';
import { ROLE12_PROFIT_OR_LOSS_LINK_ROLE } from '../../../services/mbrs/validators/link-roles';
import { recomputeRole12WithRole13Inheritance } from '../../../services/mbrs/validators/role12-statement-of-profit-or-loss.rules';
import {
  showConsolidated as showConsolidatedCells,
  hasPreviousYear as hasPreviousYearCells,
  resolvePrerequisiteValue,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import {
  } from '../../../utils/toast-durations.util';
import { computedControlPredicate, revealValidationErrors, enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

/**
 * Display label of THIS sheet. Used as `sheetLabelOverride` on the same-sheet
 * ComprehensiveIncome <-> ComprehensiveIncome_2 mismatch messages, AND as the
 * discriminator the same-sheet recheck uses to drop ONLY its own messages while
 * preserving any coexisting cross-sheet message (see runSameSheetEqualityValidations).
 * Tying the producer and the consumer to ONE constant stops them drifting — the
 * role12 `:ProfitLoss`-vs-SOCIE class of "message silently disappears" bug.
 */
const SOCI_SHEET_LABEL = 'Statement of Comprehensive Income';

/** Display label of the cross-sheet reference target (Role 19 SOCIE). Single
 *  source of truth for the cross-sheet equality message wording + every place
 *  that strips / records / preserves that message. */
const SOCIE_SHEET_LABEL = 'Statement of Changes in Equity';


/** The four dual-column (consolidation x period) cell suffixes. */
const DUAL_COLUMN_SUFFIXES: ReadonlyArray<string> = [
  '[Consolidated][CurrentYear]',
  '[Consolidated][PreviousYear]',
  '[Separate][CurrentYear]',
  '[Separate][PreviousYear]',
];

interface FieldControl {
  qname: string;
  label: string;
  type: string;
  required?: boolean;
  /** Cross-sheet conditional-required rule. Only the two `Comprehensive
   *  income, attributable to ...` rows carry one — see
   *  `validateConsolidatedRequiredFields()`. Shape mirrors
   *  `Role15RequiredIfRule` in the rules file, which the pure validator reads
   *  from the SAME schema block. */
  requiredIf?: {
    sheet: string;
    field: string;
    equals: string;
    errorMessage: string;
  };
  ui: {
    control: string;
    controlType?: string;
    level?: number;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    calculation?: string;
    note?: string;
    decimalPlaces?: number;
    /** Cross-sheet value link. `Profit (loss)` carries `{role: '310000',
     *  navigate: false}` — inherited from Role 12, no hyperlink (see
     *  `setupRole12Inheritance`). */
    linkTo?: { role: string; qname: string; navigate?: boolean };
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

// Taxonomy interfaces for calculation weights
interface TaxonomyConcept {
  Name: string;
  Label?: string;
  Type?: string;
  Weight?: number;
  Children?: TaxonomyConcept[];
  PreferredLabel?: string;
  References?: string;
  Formula?: any;
}

interface TaxonomyRole {
  Role: string;
  Definition: string;
  Concepts: TaxonomyConcept[];
}

interface UnifiedTaxonomy {
  [key: number]: TaxonomyRole;
}

// Calculation component with weight
interface CalculationComponent {
  qname: string;
  weight: number; // 1 = add, -1 = subtract
}

@Component({
  selector: 'app-role15-410000-statement-of-comprehensive-income',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    NegativeLabelPipe,
    SkeletonModule,
    FieldLinkTargetDirective,
  ],
  providers: [MessageService],
  templateUrl:
    './role15-410000-statement-of-comprehensive-income.component.html',
  styleUrls: [
    './role15-410000-statement-of-comprehensive-income.component.css',
  ],
})
export class Role15410000StatementOfComprehensiveIncomeComponent
  implements OnInit, OnDestroy
{
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private mappingStatus = inject(MappingStatusService);
  private crossRefService = inject(CrossReferenceService);
  private equalityValidation = inject(EqualityValidationService);
  private validationContext = inject(ValidationContextService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private formSchemaService = inject(FormSchemaService);
  private calculationService = inject(CalculationService);
  private cdr = inject(ChangeDetectorRef);
  private envService = inject(EnvironmentService);
  private runtimeConfig = inject(RuntimeConfigService);
  readonly fieldNoteTooltipsEnabled = this.runtimeConfig.fieldNoteTooltipsEnabled;
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  statementOfComprehensiveIncomeForm!: FormGroup;
  schema: FormSchema | null = null;

  // Cross-sheet recompute parity: the recomputed sheet bag built by
  // runEqualityValidations(), reused by validateComprehensiveIncomeAgainstRole19()
  // so OCI compares against RECOMPUTED SOCIE (not the stale persisted extraction).
  private lastRecomputedSheetBag: Record<string, any> | null = null;

  isLoading = true;
  isConsolidated = false;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Comprehensive Income (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfComprehensiveIncome
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry feeds the pure
   * role15StatementOfComprehensiveIncomeValidator, so the mounted editor and the
   * headless listing cannot drift on which columns are shown/validated.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.statementOfComprehensiveIncome);
  }

  /**
   * Show the Consolidated columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's NatureOfFinancialStatements;
   * now a live getter off the shared resolver (prereq-first, then filingInfo).
   * The util fn is imported aliased (`showConsolidatedCells`) to avoid a name
   * clash with this getter. NOTE: an unknown/unset Nature now resolves to
   * shown (the resolver's pre-subscription default) — the approved behaviour
   * change vs the old `=== 'Consolidated'` field, which collapsed unknown to
   * hidden.
   */
  get showConsolidatedColumns(): boolean {
    return showConsolidatedCells(this.dimensionVisibility);
  }

  /**
   * Show the PreviousYear columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's preparation type; now a live
   * getter off the shared resolver. The util fn is imported aliased
   * (`hasPreviousYearCells`) to avoid a name clash with this getter.
   */
  get hasPreviousYear(): boolean {
    return hasPreviousYearCells(this.dimensionVisibility);
  }

  expandedSections = new Set<string>(); // Track which sections are expanded
  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  private validationErrors = new Map<string, string[]>();
  /** qnames currently failing the schema's `requiredIf` rule. Drives the
   *  per-cell red border (`hasConsolidatedRequiredError`) and keeps the row
   *  out of the yellow "Validated*" branch — a missing mandatory value is a
   *  blocking error, not a suggested change. */
  private mandatoryFieldErrors = new Set<string>();
  /**
   * The EXACT messages the conditional-required rule last contributed to
   * `validationErrors`, per qname.
   *
   * `validationErrors` is a flat `string[]` shared by three producers (API
   * mismatch, cross-sheet equality, this rule), so removing only this rule's
   * messages means knowing which strings are ours. This ledger records them
   * instead of pattern-matching the copy.
   *
   * That distinction is load-bearing, not tidiness: the rule runs on BOTH
   * validation passes, so it must be able to withdraw its previous messages
   * before re-adding them. Recognising them by a substring of the P.O.'s
   * wording made correctness depend on the wording — reword the message and
   * the rule silently starts double-reporting and never clears. Both failures
   * are pinned by specs in the "reacts to its cross-sheet driver" block.
   */
  private mandatoryErrorMessages = new Map<string, string[]>();
  private comprehensiveIncomeMismatchErrors = new Set<string>(); // Track fields with mismatch errors for red border display
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>(); // Track equality validation errors
  /** Tracks qnames whose errors in validationErrors were written by validateCrossSheet.
   *  Lets validateSameSheet clear the map without wiping frozen cross-sheet errors. */
  private crossSheetErrorQnames = new Set<string>();
  private isLoadingState = false;
  private isCalculating = false;
  private calculatedTotalKeys: string[] = [];
  private isStateSelfUpdate = false; // Guards against setupStateListener re-validating on self-triggered state updates
  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint silently reverts a row the user has open. Mirrors
   * role23/role24, which carry the identical listener and the identical guard.
   */
  private isInitialLoadComplete = false;
  isValidationComplete = false;

  // Taxonomy data for weight-based calculations
  private unifiedTaxonomy: TaxonomyRole[] | null = null;
  // Map of calculated field qname -> array of component fields with weights
  private calculationMap = new Map<string, CalculationComponent[]>();

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  /** Bound as a property so the directive gets a stable, `this`-correct function. */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '15-410000',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.loadSchema();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize all sections as expanded by default
   */
  private initializeExpandedSections(): void {
    // Seed EVERY section header (all levels, 1–6) as expanded. Level 1 is
    // seeded — even though it has no toggle — because the shared `isRowVisible`
    // helper now consults level-1 ancestors; without it, level-1 would read as
    // collapsed and hide everything on load. Level 6 is seeded for the same
    // reason (e.g. the "Cash flow hedges" level-6 header). Seeding by
    // `isSectionHeader` (not a level allow-list) excludes leveled DATA fields
    // such as the calculated "Total comprehensive income" tagged level 4.
    if (this.schema) {
      this.schema.controls.forEach((control) => {
        if (this.isSectionHeader(control)) {
          this.expandedSections.add(control.qname);
        }
      });
    }
  }

  /**
   * Toggle section expansion
   */
  toggleSection(qname: string): void {
    if (this.expandedSections.has(qname)) {
      this.expandedSections.delete(qname);
    } else {
      this.expandedSections.add(qname);
    }
  }

  /**
   * Check if section is expanded
   */
  isSectionExpanded(qname: string): boolean {
    return this.expandedSections.has(qname);
  }

  /**
   * Check if a row should be shown based on parent section expansion.
   *
   * Delegates to the shared, pure `isRowVisible` helper (monotonic
   * strictly-decreasing ancestor walk). The `isSectionHeader` predicate is
   * passed so that DATA fields carrying a `ui.level` (e.g. the calculated
   * "Total comprehensive income" tagged level 4) are NOT mistaken for section
   * boundaries — preserving this sheet's prior header-only semantics. Replaces
   * the former per-level `findParentHeaderQname`/`getPreviousHeaderLevel` walk.
   * See `section-visibility.util.ts`.
   */
  shouldShowRow(control: FieldControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpanded(qname),
      (control) => this.isSectionHeader(control)
    );
  }

  /**
   * Column visibility (`showConsolidatedColumns` / `hasPreviousYear`) is NO
   * LONGER derived in a `checkConsolidatedStatus()` method here — it moved to
   * live getters off the shared, config-driven resolver
   * (`CrossReferenceService.getVisibility` → `dimension-visibility.util`),
   * which the pure role15StatementOfComprehensiveIncomeValidator also calls so
   * the mounted editor and the headless listing cannot drift. The resolver
   * reads Nature / preparation-type from filingPrerequisites first, then
   * filingInfo.
   */

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading = true;

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role15');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard.
    // Filename selection is shared with role15StatementOfComprehensiveIncomeValidator
    // via pickRole15SchemaFile so the editor and the pure validator cannot
    // drift apart on which schema variant is in scope. Pass the RAW accounting
    // standard (not an isMFRS-collapsed value) to the shared MFRS-default picker
    // so both paths derive the schema from the exact same literal — matches
    // role 13's pattern and avoids the isMFRS-collapse trap (a collapsed ''
    // would wrongly resolve MFRS under the MFRS-default picker).
    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole15SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then(async (data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

        // Preload schemas for sheets referenced by equality validation rules
        // so that error messages show field labels instead of raw qnames
        // IMPORTANT: await to ensure schemas are cached before validation runs
        const currentTaxonomy: 'MFRS' | 'MPERS' = this.crossRefService.isMFRS() ? 'MFRS' : 'MPERS';
        await this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);

        this.initializeExpandedSections(); // Initialize expand/collapse state after schema is loaded
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.setupEditPipeline(); // Setup reactive calculations + persist pipeline

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.statementOfComprehensiveIncomeForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupStateListener(); // Listen for external state updates (e.g., from backend API)
        this.setupRole12Inheritance(); // Keep Profit (loss) in step with Role 12
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      })
      .catch((error) => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  // Track control name mappings for duplicate qnames
  private controlNameMap = new Map<string, string>(); // Maps "qname_index" to actual control name

  /**
   * Build reactive form from schema
   * CRITICAL: Creates 4 FormControls per field (Consolidated Current/Previous + Separate Current/Previous)
   * HANDLES DUPLICATE QNAMES: If same qname appears twice, creates separate controls with index suffix
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};
    const qnameCounters = new Map<string, number>(); // Track duplicate qnames
    this.controlNameMap.clear();

    this.schema.controls.forEach((control, index) => {
      // Skip headers
      if (this.isSectionHeader(control)) return;

      // Determine required validators
      const validators: ValidatorFn[] = [];

      if (control.ui.dualColumn) {
        // Handle duplicate qnames by adding occurrence suffix
        let controlBaseName = control.qname;

        // Check if this qname already exists
        const currentCount = qnameCounters.get(control.qname) || 0;
        qnameCounters.set(control.qname, currentCount + 1);

        // If this is the second+ occurrence, add suffix
        if (currentCount > 0) {
          controlBaseName = `${control.qname}_occurrence${currentCount + 1}`;
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⚠️ Duplicate qname detected: ${control.qname}, using ${controlBaseName}`);
        }

        // Store mapping: "qname_schemaIndex" -> actual control name
        this.controlNameMap.set(`${control.qname}_${index}`, controlBaseName);

        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Always create 4 columns: Consolidated (Current, Previous) and Separate (Current, Previous)
        // All fields start disabled (editable fields can be enabled via edit mode)
        formControls[this.getXbrlControlName(controlBaseName, 'Consolidated', 'Current')] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[this.getXbrlControlName(controlBaseName, 'Consolidated', 'Previous')] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[this.getXbrlControlName(controlBaseName, 'Separate', 'Current')] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[this.getXbrlControlName(controlBaseName, 'Separate', 'Previous')] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
      }
    });

    this.statementOfComprehensiveIncomeForm = this.fb.group(formControls);
  }

  /**
   * Get the actual form control base name for a control at a specific schema index
   * Handles duplicate qnames by returning the correct occurrence suffix
   * Used by template to bind to the correct form control
   */
  getControlBaseName(control: FieldControl, schemaIndex: number): string {
    const mapKey = `${control.qname}_${schemaIndex}`;
    return this.controlNameMap.get(mapKey) || control.qname;
  }

  /**
   * Check if control is a section header (including TextBlock)
   */
  isSectionHeader(control: FieldControl): boolean {
    return (
      control.type === 'SectionHeader' ||
      control.type === 'TextBlock' ||
      control.ui.control === 'header' ||
      control.ui.controlType === 'section-header'
    );
  }

  /**
   * PHASE 2: Get XBRL control name using simplified bracket notation
   * Converts from old concatenated format to new bracket format
   *
   * OLD: qnameConsolidatedCurrent
   * NEW: qname[Consolidated][CurrentYear]
   *
   * @param qname The XBRL concept name
   * @param consolidatedOrSeparate 'Consolidated' or 'Separate'
   * @param period 'Current' or 'Previous'
   * @returns Control name in bracket format
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * Get header CSS class based on level. Delegates to the shared
   * `headerLevelClass`, which maps a numeric or `'hN'` level 1:1 to
   * `header-level-N` (so new depths need only a matching CSS rule) and falls
   * back to `header-level-1`.
   */
  getHeaderClass(level?: number | string): string {
    return headerLevelClass(level);
  }

  /**
   * Whether the section header at `index` is collapsible — i.e. it renders a
   * chevron and toggles its descendant rows on click. Delegates to the shared,
   * structural `isCollapsibleHeader` rule (any header deeper than the level-1
   * banner that actually encloses rows — no level ceiling, so it can't drift as
   * the schema gains deeper headers; this is what left the level-6 categories
   * without a show/hide button). Uses the default `minCollapsibleLevel` of 2, so
   * the level-1 "Comprehensive income net of tax" banner stays non-toggled.
   */
  isCollapsibleHeader(index: number): boolean {
    return !!this.schema && isCollapsibleHeaderRule(
      this.schema.controls,
      index,
      (control) => this.isSectionHeader(control),
    );
  }

  /**
   * Check if field is calculated
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory`, so this sheet answers the
   * question the same way as the other 16.
   *
   * **This sheet is why "no mandatory fields today" is not a reason to skip
   * wiring the marker.** When the marker seam rolled out (2026-08-11) this
   * sheet declared zero mandatory controls, so it was classified "nothing to
   * do" and left unwired. Hours later the PO's enumerated totals list flagged
   * its calculated totals — and the flags landed with nowhere to render, so
   * the `*` silently did not appear until smoke testing caught it. Wire the
   * marker whenever a sheet renders schema-driven labels, whatever its current
   * mandatory count. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves;
   * same-sheet `selfData` is deliberately NOT passed, because this sheet keys
   * its values per cell (`qname[Dimension][Period]`) rather than by bare
   * qname, so a same-sheet lookup would silently miss. Add it — with the right
   * key shape — if a same-sheet conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      // Same shared resolution rule as `validateConsolidatedRequiredFields()`
      // (prereq-first, `''` counts as absent), so the `*` can never be missing
      // from a row that is already raising a mandatory error.
      crossSheetValue: (sheet, field) =>
        sheet === SHEET_KEYS.filingPrerequisites
          ? resolvePrerequisiteValue(field, {
              filingPrerequisites: this.stateService.getSheetData(
                SHEET_KEYS.filingPrerequisites
              ),
              filingInfo: this.stateService.getSheetData(SHEET_KEYS.filingInfo),
            })
          : (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Wire the live (in-edit) feedback pipeline:
   *   calc → validateSameSheet → validateCrossSheet → evaluateStatus.
   * validateCrossSheet is display-only (inline cross-sheet errors live on the
   * active tab); no persist — that runs on Tick (saveEditMode).
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.statementOfComprehensiveIncomeForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      calculate: () => {
        this.calculateTotals();
        this.autoCheckComprehensiveIncomeMatch();
      },
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /** SAME-SHEET validation — safe to run live during edit.
   *  Clears validationErrors EXCEPT frozen cross-sheet keys, then runs same-sheet methods. */
  private validateSameSheet(): void {
    // Clear all keys EXCEPT those frozen by the last validateCrossSheet
    for (const key of [...this.validationErrors.keys()]) {
      if (!this.crossSheetErrorQnames.has(key)) {
        this.validationErrors.delete(key);
      }
    }
    this.validateAgainstApiData();
    const isMFRS = this.schema?.controls.some(
      (control) => control.qname === 'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation'
    ) ? true : false;
    this.runSameSheetEqualityValidations(isMFRS ? 'MFRS' : 'MPERS');
    this.validateDecimalFormat();
    // Runs on the same-sheet pass because the rule inspects THIS sheet's
    // cells: without it a just-typed value would keep its red border until the
    // next Tick. It ALSO runs in `validateCrossSheet()`, because its driver
    // lives on another sheet — see the note there.
    this.validateConsolidatedRequiredFields();
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /** CROSS-SHEET validation — runs only on Tick and from cross-sheet listeners.
   *  Strips prior cross-sheet keys from validationErrors, rebuilds, records new keys. */
  private validateCrossSheet(): void {
    // Remove prior cross-sheet entries from the map
    for (const key of this.crossSheetErrorQnames) {
      const existing = this.validationErrors.get(key);
      if (existing) {
        const stripped = existing.filter(
          (message) => !message.includes(SOCIE_SHEET_LABEL)
        );
        if (stripped.length > 0) {
          this.validationErrors.set(key, stripped);
        } else {
          this.validationErrors.delete(key);
        }
      }
    }
    this.crossSheetErrorQnames.clear();

    // Snapshot the keys already in the map before cross-sheet methods run
    const keysBefore = new Set(this.validationErrors.keys());
    this.runEqualityValidations();
    this.validateComprehensiveIncomeAgainstRole19();
    // The conditional-required rule runs on BOTH passes, and this one is the
    // load-bearing half. Its DRIVER is cross-sheet
    // (`NatureOfFinancialStatements`, owned by Filing Info in Phase 2 and
    // Filing Prerequisites in Phase 1), and the state watcher that reacts to a
    // change in that slice calls `validateCrossSheet()` — nothing else. Without
    // this line, switching Nature to "Consolidated" with the sheet already
    // mounted flips the sheet-level tick to Failed (the pure validator DOES
    // evaluate the rule, via the container's `recomputePureVerdictStatuses`)
    // while the rows underneath stay clean — the editor and the listing
    // disagreeing on the same data. Idempotent: the rule drops its own prior
    // messages before re-adding them, so running it on both passes cannot
    // double-report.
    this.validateConsolidatedRequiredFields();
    // Record every qname that cross-sheet methods added or modified
    for (const key of this.validationErrors.keys()) {
      if (!keysBefore.has(key)) {
        this.crossSheetErrorQnames.add(key);
      }
    }
    // Also record qnames that were modified (extra errors appended to existing keys)
    for (const key of keysBefore) {
      const current = this.validationErrors.get(key);
      if (current) {
        // If runEqualityValidations/validateComprehensiveIncomeAgainstRole19 appended errors,
        // they are tagged with the cross-sheet target label (SOCIE_SHEET_LABEL).
        const hasCrossMsg = current.some(
          (message) => message.includes(SOCIE_SHEET_LABEL)
        );
        if (hasCrossMsg) this.crossSheetErrorQnames.add(key);
      }
    }
  }

  /** Returns true if an error message originates from runEqualityValidations cross-sheet rules. */
  private _isEqualityRuleError(msg: string): boolean {
    return msg.includes('Mismatch in value') && msg.includes(SOCIE_SHEET_LABEL);
  }

  /** Full union validation — same-sheet then cross-sheet. */
  private runAllValidations(): void {
    this.validationErrors.clear();
    this.mandatoryFieldErrors.clear();
    this.mandatoryErrorMessages.clear();
    this.crossSheetErrorQnames.clear();

    this.validateAgainstApiData();
    this.runEqualityValidations();
    this.validateDecimalFormat();
    this.validateConsolidatedRequiredFields();
    this.validateComprehensiveIncomeAgainstRole19();

    // Record cross-sheet qnames from the full run
    for (const key of this.validationErrors.keys()) {
      const msgs = this.validationErrors.get(key)!;
      if (msgs.some((message) =>
        message.includes(SOCIE_SHEET_LABEL) ||
        this._isEqualityRuleError(message)
      )) {
        this.crossSheetErrorQnames.add(key);
      }
    }

    // Single change detection after ALL validations complete
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Keep `Profit (loss)` in step with the Statement of Profit or Loss (Role 12).
   *
   * WHY THIS EXISTS — this sheet's `Profit (loss)` is not its own datum: it is
   * Role 12's `Profit (loss)` total. Both role-410000 schemas said so all along
   * ("Automatically populated from Role-12"), but the declaration sat in a
   * TOP-LEVEL `linkTo` block no code read, so the value was hydrated once from
   * the N8N extraction and then frozen — editing Role 12 moved its total and
   * this sheet never reacted, silently poisoning `Total comprehensive income`
   * (`ProfitLoss + OtherComprehensiveIncome`) and the blocking SOCIE equality
   * that reads it. P.O. verdict, 2026-08-21.
   *
   * WATCHES TWO SLICES, NOT ONE. The chain is SOCI <- SOPL <- Analysis of P&L,
   * so a Role-13 edit changes this sheet's value without Role 12's slice moving
   * at all. `pullRole12Values()` recomputes Role 12 itself (see there), so both
   * slices are triggers.
   *
   * `state$` and `modifiedState$` are the SAME subject (`state$` is a
   * back-compat alias of `modifiedStateSubject`), so one subscription covers
   * live edits, cross-tab adoption, and backend hydration alike — no separate
   * settle timer is needed, because nothing here waits for another sheet to
   * recompute on our behalf.
   *
   * The headless counterpart is `role15StatementOfComprehensiveIncomeValidator`'s
   * `inheritProfitLossFromRole12` (and `resolveInheritedLeaves` in
   * `cross-sheet-recompute.util.ts`) — all three replay the same recipe, and
   * must move together or the listing verdict and the open sheet disagree.
   */
  private setupRole12Inheritance(): void {
    // Whatever the two source slices already hold.
    this.pullRole12Values();

    this.stateService.state$
      .pipe(
        map(
          (state) =>
            [state.statementOfProfitOrLoss, state.analysisOfProfitOrLoss] as const
        ),
        // Slice identity changes only when that slice is rewritten, so this
        // filters out emissions caused by the other 24 sheets.
        distinctUntilChanged(
          ([prevRole12, prevRole13], [role12, role13]) =>
            prevRole12 === role12 && prevRole13 === role13
        ),
        debounceTime(STATE_INHERITANCE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.pullRole12Values());
  }

  /**
   * Pull Role 12's `Profit (loss)` into this form.
   *
   * Keyed on `ui.linkTo.role === '310000'` — the same shape Role 8 and Role 12
   * use for their own inheritance, and the same key `applyLinkedInheritance`
   * matches on in the headless path, so the editor and the pure validator can
   * never disagree about WHICH cells are inherited. The link additionally
   * carries `navigate: false`, which suppresses the row-label hyperlink without
   * touching the value (P.O. deferred navigation to a later pass) — see
   * `resolveFieldLinkTarget`.
   *
   * **Role 12 is RECOMPUTED here, never read raw** — the fix for the reported
   * two-tab defect (SOCI open in one tab, Analysis of Profit or Loss in the
   * other: editing the analysis left this sheet stale until the other tab was
   * switched to SOPL). Nothing recomputes Role 12's persisted `Profit (loss)`
   * while Role 12 is UNMOUNTED: the container recomputes it only on the
   * API-load path, and otherwise only Role 12's own editor does, on mount. So
   * the raw slice lags its own Role-13 leaves for as long as nobody opens it.
   * `recomputeRole12WithRole13Inheritance` replays exactly what a mounted Role
   * 12 would compute — its Role-13 leaves inherited (themselves recomputed
   * first), then its own totals — and is the single copy of that recipe, shared
   * with both pure validators.
   *
   * Two further deliberate details:
   *   - the pulled cells are registered as CALCULATED keys, so `isSheetModified`
   *     skips them and an upstream edit never lights this sheet's pencil — the
   *     user did not touch this sheet. (Role 12 gets the same outcome a
   *     different way: it strips its inherited leaves out of the persisted slice
   *     entirely. Here the value must stay in the slice, because Role 15's
   *     `Profit (loss)` is a submitted XBRL fact.)
   *   - `emitEvent: false` keeps the edit pipeline from treating the pull as a
   *     user edit; `calculateTotals()` is therefore called explicitly.
   */
  private pullRole12Values(): void {
    if (!this.schema) return;

    const linkedControls = this.schema.controls.filter(
      (control) => control.ui.linkTo?.role === ROLE12_PROFIT_OR_LOSS_LINK_ROLE
    );
    if (linkedControls.length === 0) return;

    const role12Data = this.stateService.getSheetData(
      SHEET_KEYS.statementOfProfitOrLoss
    );
    if (!role12Data || Object.keys(role12Data).length === 0) return;

    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const { snapshot } = recomputeRole12WithRole13Inheritance(
      role12Data,
      this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLoss),
      this.xbrlPrefix,
      accountingStandard
    );

    const inheritedKeys: string[] = [];
    let changed = false;

    for (const control of linkedControls) {
      const sourceQname = control.ui.linkTo!.qname;

      for (const suffix of DUAL_COLUMN_SUFFIXES) {
        const sourceValue = snapshot[`${sourceQname}${suffix}`];
        if (sourceValue === undefined || sourceValue === null) continue;

        const controlName = `${control.qname}${suffix}`;
        const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);
        if (!formControl) continue;

        inheritedKeys.push(controlName);
        if (formControl.value !== sourceValue) {
          formControl.setValue(sourceValue, { emitEvent: false });
          changed = true;
        }
        // Inherited, never typed — must not read as 'Edited'.
        formControl.markAsPristine();
      }
    }

    if (inheritedKeys.length > 0) {
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.statementOfComprehensiveIncome,
        inheritedKeys
      );
    }

    if (!changed) return;

    this.calculateTotals();

    // Publish so SOCIE's cross-sheet check reads the new Total comprehensive
    // income even if the user never touches this sheet. Skipped during init —
    // loadSavedState owns the first write and would otherwise race it.
    if (this.isInitialLoadComplete && !this.isLoadingState) {
      this.isStateSelfUpdate = true;
      this.stateService.updateSheetData(
        SHEET_KEYS.statementOfComprehensiveIncome,
        this.statementOfComprehensiveIncomeForm.getRawValue(),
        true,
        true // skipEditedMark — inheritance is not a user edit
      );
      this.isStateSelfUpdate = false;
    }
  }

  /**
   * Taxonomy prefix for every calculation on this sheet, detected from the
   * LOADED SCHEMA rather than re-derived from the accounting standard.
   *
   * Schema detection is the safer of the two: `loadSchema()` already picked the
   * variant from the accounting standard, so reading it back cannot disagree
   * with the controls actually rendered — whereas re-deriving would reintroduce
   * the MFRS-default trap (an empty/unknown standard silently selecting
   * `ifrs-full`) at a second, independent site. Shared by `calculateTotals()`
   * and `pullRole12Values()` so the inherited cell's key and the totals that
   * consume it can never use different prefixes.
   */
  private get xbrlPrefix(): 'ifrs-full' | 'ifrs-smes' {
    return this.schema?.controls.some(
      (control) =>
        control.qname ===
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation'
    )
      ? 'ifrs-full'
      : 'ifrs-smes';
  }

  /**
   * Calculate totals for Statement of Comprehensive Income (MFRS and MPERS)
   * Detects schema type and uses appropriate QNames
   */
  private calculateTotals(): void {
    if (!this.schema) return;

    this.isCalculating = true;

    const prefix = this.xbrlPrefix;

    // Get all current form values
    const allFormData = this.statementOfComprehensiveIncomeForm.getRawValue();

    // Call the calculation service
    const calculatedTotals = this.calculationService.calculateTotals_410000(
      allFormData,
      prefix
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfComprehensiveIncome,
      Object.keys(calculatedTotals)
    );

    // Apply calculated values to form controls
    Object.entries(calculatedTotals).forEach(([controlName, value]) => {
      const control = this.statementOfComprehensiveIncomeForm.get(controlName);
      if (control) {
        control.setValue(value, { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });

    this.isCalculating = false;
  }

  // Old calculation code commented out - now using CalculationService
  private calculateTotals_OLD(): void {
    if (!this.schema) return;

    // Detect if MFRS or MPERS based on schema controls
    const isMFRS = this.schema.controls.some(
      (control) =>
        control.qname ===
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation'
    );

    // Always calculate for all 4 columns
    const suffixes = [
      'ConsolidatedCurrent',
      'ConsolidatedPrevious',
      'SeparateCurrent',
      'SeparatePrevious',
    ];

    if (isMFRS) {
      // MFRS Calculation Logic
      suffixes.forEach((suffix) => {
        // 1. Calculated subtotal: Other items (NOT reclassified) = sum of 3 fields
        const otherItemsNotReclassified =
          this.getFieldValue(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation',
            suffix
          ) +
          this.getFieldValue(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRemeasurementsOfDefinedBenefitPlans',
            suffix
          ) +
          this.getFieldValue(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesFromInvestmentsInEquityInstruments',
            suffix
          );

        const otherItemsNotReclassifiedControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxOtherEquityInstrumentsThatWillNotBeReclassifiedToProfitOrLoss' +
              suffix
          );
        if (otherItemsNotReclassifiedControl) {
          otherItemsNotReclassifiedControl.setValue(otherItemsNotReclassified, {
            emitEvent: false,
          });
          // Mark as pristine to prevent calculated fields from showing 'Edited' status
          otherItemsNotReclassifiedControl.markAsPristine();
        }

        // 2. Total NOT reclassified = 3 input fields + calculated subtotal + Share of OCI
        const totalNotReclassified =
          this.getFieldValue(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxOtherEquityInstrumentsThatWillNotBeReclassifiedToProfitOrLoss',
            suffix
          ) +
          this.getFieldValue(
            'ifrs-full:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
            suffix
          );

        const totalNotReclassifiedControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeThatWillNotBeReclassifiedToProfitOrLossNetOfTax' +
              suffix
          );
        if (totalNotReclassifiedControl) {
          totalNotReclassifiedControl.setValue(totalNotReclassified, {
            emitEvent: false,
          });
          totalNotReclassifiedControl.markAsPristine();
        }

        // 3. Cash flow hedges subtotal
        const cashFlowHedges =
          this.getFieldValue(
            'ifrs-full:GainsLossesOnCashFlowHedgesNetOfTax',
            suffix
          ) -
          this.getFieldValue(
            'ifrs-full:ReclassificationAdjustmentsOnCashFlowHedgesNetOfTax',
            suffix
          );

        const cashFlowHedgesControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxCashFlowHedges' + suffix
          );
        if (cashFlowHedgesControl) {
          cashFlowHedgesControl.setValue(cashFlowHedges, { emitEvent: false });
          cashFlowHedgesControl.markAsPristine();
        }

        // 4. Hedges of net investments subtotal
        const hedgesOfNetInvestments =
          this.getFieldValue(
            'ifrs-full:GainsLossesOnHedgesOfNetInvestmentsInForeignOperationsNetOfTax',
            suffix
          ) -
          this.getFieldValue(
            'ifrs-full:ReclassificationAdjustmentsOnHedgesOfNetInvestmentsInForeignOperationsNetOfTax',
            suffix
          );

        const hedgesOfNetInvestmentsControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxHedgesOfNetInvestmentsInForeignOperations' +
              suffix
          );
        if (hedgesOfNetInvestmentsControl) {
          hedgesOfNetInvestmentsControl.setValue(hedgesOfNetInvestments, {
            emitEvent: false,
          });
          hedgesOfNetInvestmentsControl.markAsPristine();
        }

        // 5. Exchange differences subtotal
        const exchangeDifferences =
          this.getFieldValue(
            'ifrs-full:GainsLossesOnExchangeDifferencesOnTranslationNetOfTax',
            suffix
          ) -
          this.getFieldValue(
            'ifrs-full:ReclassificationAdjustmentsOnExchangeDifferencesOnTranslationNetOfTax',
            suffix
          );

        const exchangeDifferencesControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation' +
              suffix
          );
        if (exchangeDifferencesControl) {
          exchangeDifferencesControl.setValue(exchangeDifferences, {
            emitEvent: false,
          });
          exchangeDifferencesControl.markAsPristine();
        }

        // 6. FVOCI subtotal
        const fvoci =
          this.getFieldValue(
            'ifrs-full:GainsLossesOnFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncomeNetOfTax',
            suffix
          ) -
          this.getFieldValue(
            'ifrs-full:ReclassificationAdjustmentsOnFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncomeNetOfTax',
            suffix
          ) -
          this.getFieldValue(
            'ifrs-full:AmountsRemovedFromEquityAndAdjustedAgainstFairValueOfFinancialAssetsOnReclassificationOutOfFairValueThroughOtherComprehensiveIncomeMeasurementCategoryNetOfTax',
            suffix
          );

        const fvociControl = this.statementOfComprehensiveIncomeForm.get(
          'ifrs-full:OtherComprehensiveIncomeNetOfTaxFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncome' +
            suffix
        );
        if (fvociControl) {
          fvociControl.setValue(fvoci, { emitEvent: false });
          fvociControl.markAsPristine();
        }

        // 7. Total WILL BE reclassified = all subtotals + Share of OCI + Other items
        const totalWillBeReclassified =
          cashFlowHedges +
          hedgesOfNetInvestments +
          exchangeDifferences +
          fvoci +
          this.getFieldValue(
            'ifrs-full:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillBeReclassifiedToProfitOrLossNetOfTax',
            suffix
          ) +
          this.getFieldValue(
            'ssmt-mfrs:OtherComprehensiveIncomeNetOfTaxGainsLossesOnOtherItems',
            suffix
          );

        const totalWillBeReclassifiedControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:OtherComprehensiveIncomeThatWillBeReclassifiedToProfitOrLossNetOfTax' +
              suffix
          );
        if (totalWillBeReclassifiedControl) {
          totalWillBeReclassifiedControl.setValue(totalWillBeReclassified, {
            emitEvent: false,
          });
          totalWillBeReclassifiedControl.markAsPristine();
        }

        // 8. Total OCI = Total NOT + Total WILL BE
        const totalOCI = totalNotReclassified + totalWillBeReclassified;
        const totalOCIControl = this.statementOfComprehensiveIncomeForm.get(
          'ifrs-full:OtherComprehensiveIncome' + suffix
        );
        if (totalOCIControl) {
          totalOCIControl.setValue(totalOCI, { emitEvent: false });
          totalOCIControl.markAsPristine();
        }

        // 9. Total comprehensive income = Profit/Loss + Total OCI
        const profitLoss = this.getFieldValue('ifrs-full:ProfitLoss', suffix);
        const totalComprehensiveIncome = profitLoss + totalOCI;
        const totalComprehensiveIncomeControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:ComprehensiveIncome' + suffix
          );
        if (totalComprehensiveIncomeControl) {
          totalComprehensiveIncomeControl.setValue(totalComprehensiveIncome, {
            emitEvent: false,
          });
          totalComprehensiveIncomeControl.markAsPristine();
        }

        // 10. Total comprehensive income attribution (Owners + NCI)
        // This is the SECOND occurrence of ComprehensiveIncome in the schema
        // Uses the _2 suffix as defined in the schema (ifrs-full:ComprehensiveIncome_2)
        const comprehensiveIncomeToOwners = this.getFieldValue(
          'ifrs-full:ComprehensiveIncomeAttributableToOwnersOfParent',
          suffix
        );
        const comprehensiveIncomeToNCI = this.getFieldValue(
          'ifrs-full:ComprehensiveIncomeAttributableToNoncontrollingInterests',
          suffix
        );
        const totalAttribution =
          comprehensiveIncomeToOwners + comprehensiveIncomeToNCI;

        // Use the second occurrence control (uses _2 suffix as defined in schema)
        const totalAttributionControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-full:ComprehensiveIncome_2' + suffix
          );

        if (totalAttributionControl) {
          // Verify attribution matches calculated total (validation)
          if (Math.abs(totalComprehensiveIncome - totalAttribution) > 0.01) {
            console.warn(
              `⚠️ Attribution mismatch for ${suffix}: ` +
              `Calculated=${totalComprehensiveIncome}, Attribution=${totalAttribution}`
            );
          }

          // Set attribution total (this is displayed in the second row)
          totalAttributionControl.setValue(totalAttribution, {
            emitEvent: false,
          });
          totalAttributionControl.markAsPristine();
        }
      });
    } else {
      // MPERS Calculation Logic
      suffixes.forEach((suffix) => {
        // 1. Total other comprehensive income that will NOT be reclassified to profit or loss, net of tax
        const totalNotReclassified =
          this.getFieldValue(
            'ifrs-smes:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation',
            suffix
          ) +
          this.getFieldValue(
            'ifrs-smes:OtherComprehensiveIncomeNetOfTaxActuarialGainsLossesOnDefinedBenefitPlans',
            suffix
          ) +
          this.getFieldValue(
            'ifrs-smes:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation',
            suffix
          ) +
          this.getFieldValue(
            'ssmt-mpers:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
            suffix
          );

        const totalNotReclassifiedMPERSControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ssmt-mpers:OtherComprehensiveIncomeThatWillNotBeReclassifiedToProfitOrLossNetOfTax' +
              suffix
          );
        if (totalNotReclassifiedMPERSControl) {
          totalNotReclassifiedMPERSControl.setValue(totalNotReclassified, {
            emitEvent: false,
          });
          totalNotReclassifiedMPERSControl.markAsPristine();
        }

        // 2. Total other comprehensive income that WILL BE reclassified to profit or loss, net of tax
        const totalWillBeReclassified =
          this.getFieldValue(
            'ssmt-mpers:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation',
            suffix
          ) +
          this.getFieldValue(
            'ssmt-mpers:OtherComprehensiveIncomeNetOfTaxChangeInFairValueOfHedgingInstrument',
            suffix
          ) +
          this.getFieldValue(
            'ssmt-mpers:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillBeReclassifiedToProfitOrLossNetOfTax',
            suffix
          );

        const totalWillBeReclassifiedMPERSControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ssmt-mpers:OtherComprehensiveIncomeThatWillBeReclassifiedToProfitOrLossNetOfTax' +
              suffix
          );
        if (totalWillBeReclassifiedMPERSControl) {
          totalWillBeReclassifiedMPERSControl.setValue(
            totalWillBeReclassified,
            { emitEvent: false }
          );
          totalWillBeReclassifiedMPERSControl.markAsPristine();
        }

        // 3. Total other comprehensive income (includes Other Items)
        const otherItems = this.getFieldValue(
          'ssmt-mfrs:OtherComprehensiveIncomeNetOfTaxGainsLossesOnOtherItems',
          suffix
        );
        const totalOCI =
          totalNotReclassified + totalWillBeReclassified + otherItems;
        const totalOCIMPERSControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-smes:OtherComprehensiveIncome' + suffix
          );
        if (totalOCIMPERSControl) {
          totalOCIMPERSControl.setValue(totalOCI, { emitEvent: false });
          totalOCIMPERSControl.markAsPristine();
        }

        // 4. Total comprehensive income (Profit/Loss + Total OCI)
        const profitLoss = this.getFieldValue('ifrs-smes:ProfitLoss', suffix);
        const totalComprehensiveIncome = profitLoss + totalOCI;
        const totalComprehensiveIncomeMPERSControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-smes:ComprehensiveIncome' + suffix
          );
        if (totalComprehensiveIncomeMPERSControl) {
          totalComprehensiveIncomeMPERSControl.setValue(
            totalComprehensiveIncome,
            { emitEvent: false }
          );
          totalComprehensiveIncomeMPERSControl.markAsPristine();
        }

        // 5. Total comprehensive income attribution (Owners + NCI)
        // This is the SECOND occurrence of ComprehensiveIncome in the MPERS schema
        // Uses the _2 suffix as defined in the schema (ifrs-smes:ComprehensiveIncome_2)
        const comprehensiveIncomeToOwners = this.getFieldValue(
          'ifrs-smes:ComprehensiveIncomeAttributableToOwnersOfParent',
          suffix
        );
        const comprehensiveIncomeToNCI = this.getFieldValue(
          'ifrs-smes:ComprehensiveIncomeAttributableToNoncontrollingInterests',
          suffix
        );
        const totalAttribution =
          comprehensiveIncomeToOwners + comprehensiveIncomeToNCI;

        // Use the second occurrence control (uses _2 suffix as defined in schema)
        const totalAttributionMPERSControl =
          this.statementOfComprehensiveIncomeForm.get(
            'ifrs-smes:ComprehensiveIncome_2' + suffix
          );

        if (totalAttributionMPERSControl) {
          // Verify attribution matches calculated total (validation)
          if (Math.abs(totalComprehensiveIncome - totalAttribution) > 0.01) {
            console.warn(
              `⚠️ MPERS Attribution mismatch for ${suffix}: ` +
              `Calculated=${totalComprehensiveIncome}, Attribution=${totalAttribution}`
            );
          }

          // Set attribution total (this is displayed in the second row)
          totalAttributionMPERSControl.setValue(totalAttribution, {
            emitEvent: false,
          });
          totalAttributionMPERSControl.markAsPristine();
        }
      });
    }

    // After all calculations are done, validate against API baseline
    if (!this.isLoadingState && !this.isCalculating) {
      this.validateAgainstApiData();
    }
  }

  /**
   * Validate calculated totals against API baseline data
   * Compares calculated sums with the original API values to detect discrepancies
   *
   * IMPORTANT: This method only manages its OWN error entries (API mismatch errors
   * containing "does not match the extracted sum"). It preserves ALL other error types
   * (cross-sheet, equality, mandatory, same-sheet mismatch) set by other validation methods.
   * It does NOT call cdr.detectChanges() — the caller is responsible for triggering CD
   * after all validation methods have run.
   */
  private validateAgainstApiData(): void {
    if (!this.schema) return;

    // role15 control names are bracket-suffix concatenated:
    // qname + '[Consolidated][CurrentYear]' etc. (getXbrlControlName format).
    // ALL 4 columns are checked regardless of render visibility — the badge
    // / tooltip helpers (getRowValidationStatus / getRowErrorDescription) gate
    // the display by visible-column label, so these label strings must match
    // the exact strings those helpers expect: 'Consolidated Current',
    // 'Consolidated Previous', 'Separate Current', 'Separate Previous'.
    const columns: { key: string; label: string }[] = [
      { key: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
      { key: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
      { key: '[Separate][CurrentYear]', label: 'Separate Current' },
      { key: '[Separate][PreviousYear]', label: 'Separate Previous' },
    ];

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfComprehensiveIncome,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.statementOfComprehensiveIncomeForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Run equality validation rules from schema
   * Validates fields against their equality rules (same-sheet validation)
   */
  private runEqualityValidations(): void {
    if (!this.schema) return;

    // Detect taxonomy (MFRS or MPERS)
    const isMFRS = this.schema.controls.some(
      (control) => control.qname === 'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation'
    );
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Get data from all sheets via state service (for cross-sheet validation)
    const allSheetData = this.stateService.exportAllData();

    // Cross-sheet recompute parity: recompute the referenced sheets
    // (SOCIE, P&L, …) totals before comparing against them, so the comparison
    // reads fresh (not stale-extracted) operands. The SOCIE schema is cached by
    // preloadReferencedSchemas (SOCIE is in this sheet's equality rules), so its
    // equityColumns + column visibility are available here. The recomputed bag
    // is stashed on `lastRecomputedSheetBag` so the bespoke
    // validateComprehensiveIncomeAgainstRole19() reuses the SAME recomputed
    // SOCIE (recompute once, reuse).
    const socieSchemaForCtx = this.validationContext.getCachedSchema(
      this.validationContext.resolveSheetKey(SHEET_KEYS.statementOfChangesInEquity) ?? SHEET_KEYS.statementOfChangesInEquity
    );
    const socieColumnContext = buildSocieColumnContext(
      this.crossRefService.isConsolidated(),
      this.crossRefService.is1stTimePreparation(),
      socieSchemaForCtx?.equityColumns
    );
    const referenced = referencedSheetKeysFromSchema(this.schema, SHEET_KEYS.statementOfComprehensiveIncome);
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.statementOfComprehensiveIncome]: this.statementOfComprehensiveIncomeForm.getRawValue() },
      referenced,
      currentTaxonomy,
      // Referenced SOCIE operand is recompute-of-latest — never the N8N snapshot
      // (revised global rule); matches the pure validator.
      (key) => this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key),
      socieColumnContext
    );
    this.lastRecomputedSheetBag = formValuesWithAllSheets;

    // Pass SHEET_KEYS.statementOfComprehensiveIncome as currentSheetKey
    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfComprehensiveIncome
    );

    // Store errors
    this.equalityValidationErrors = new Map(Object.entries(errors));

    // Also add to validationErrors Map for display compatibility
    Object.keys(errors).forEach(qname => {
      const errorMessages = errors[qname].map((err: any) => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Keep only API mismatch warnings (contain EXTRACTED_SUM_MARKER)
      const apiMismatchWarnings = existingErrors.filter(msg =>
        msg.includes(EXTRACTED_SUM_MARKER)
      );
      this.validationErrors.set(qname, [...apiMismatchWarnings, ...errorMessages]);
    });

    // Same-sheet equality validation for bracket-notation fields (column-by-column)
    // The generic service can't resolve bracket-notation keys like qname[Consolidated][CurrentYear],
    // so we handle same-sheet equality comparisons directly here.
    this.runSameSheetEqualityValidations(currentTaxonomy);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Equality validation completed (role15):', {
    //   errorsFound: this.equalityValidationErrors.size,
    //   errors: Object.fromEntries(this.equalityValidationErrors)
    // });
  }

  /**
   * Same-sheet equality validation for bracket-notation fields (column-by-column)
   * Handles ComprehensiveIncome vs ComprehensiveIncome_2 comparison per column.
   * The generic validation service cannot resolve bracket-notation form control keys,
   * so this method performs the comparison directly using getXbrlControlName().
   */
  private runSameSheetEqualityValidations(currentTaxonomy: 'MFRS' | 'MPERS'): void {
    if (!this.schema) return;

    const isMFRS = currentTaxonomy === 'MFRS';

    // Build pairs from schema equality rules that target the same sheet
    const sameSheetPairs: Array<{ sourceQname: string; targetQname: string; sourceLabel: string; targetLabel: string }> = [];

    this.schema.controls.forEach((control: any) => {
      if (!control.validation?.equality) return;

      control.validation.equality.forEach((rule: any) => {
        if (rule.sheet === SHEET_KEYS.statementOfComprehensiveIncome) {
          // Find the target control's label in the schema
          const targetControl = this.schema!.controls.find((control: any) => control.qname === rule.field);
          sameSheetPairs.push({
            sourceQname: control.qname,
            targetQname: rule.field,
            sourceLabel: control.label || control.qname,
            targetLabel: targetControl?.label || rule.field,
          });
        }
      });
    });

    if (sameSheetPairs.length === 0) return;

    // Clear previously added SAME-SHEET mismatch messages to avoid accumulation on
    // repeated calls, then regenerate them below. This recheck runs live on every
    // edit cycle, so it must drop ONLY the messages it itself owns and PRESERVE
    // everything else on the key — extracted-sum suggested-change warnings AND any
    // coexisting CROSS-SHEET equality message (ComprehensiveIncome carries both a
    // same-sheet equality vs ComprehensiveIncome_2 and a cross-sheet equality vs
    // SOCIE).
    //
    // It used to WHITELIST the SOCIE label string, which silently dropped a cross-
    // sheet message to any OTHER target sheet (the role12 `:ProfitLoss`-vs-SOCIE
    // disappearing-message class). We now BLACKLIST only this sheet's OWN same-sheet
    // mismatch — a `MISMATCH_CORE` message tagged with this sheet's label
    // (`SOCI_SHEET_LABEL`, the same constant passed as `sheetLabelOverride` in
    // getComprehensiveIncomeMismatchMessage). Producer + discriminator share one
    // constant, so they can never drift; cross-sheet messages to ANY sheet survive.
    const affectedQnames = new Set<string>();
    sameSheetPairs.forEach(({ sourceQname, targetQname }) => {
      affectedQnames.add(sourceQname);
      affectedQnames.add(targetQname);
    });
    affectedQnames.forEach(qname => {
      const existing = this.validationErrors.get(qname) || [];
      const filtered = existing.filter(msg => !this.isOwnSameSheetMismatch(msg));
      if (filtered.length > 0) {
        this.validationErrors.set(qname, filtered);
      } else {
        this.validationErrors.delete(qname);
      }
    });

    const columnMappings: Array<{ consolidatedOrSeparate: 'Consolidated' | 'Separate'; period: 'Current' | 'Previous'; label: string; visible: boolean }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', label: 'Consolidated Current', visible: this.showConsolidatedColumns },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', label: 'Consolidated Previous', visible: this.showConsolidatedColumns && this.hasPreviousYear },
      { consolidatedOrSeparate: 'Separate', period: 'Current', label: 'Separate Current', visible: true },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', label: 'Separate Previous', visible: this.hasPreviousYear },
    ];

    // Track already-processed pairs to avoid duplicating errors (A→B and B→A)
    const processedPairs = new Set<string>();

    sameSheetPairs.forEach(({ sourceQname, targetQname, sourceLabel, targetLabel }) => {
      const pairKey = [sourceQname, targetQname].sort().join('|');
      if (processedPairs.has(pairKey)) return;
      processedPairs.add(pairKey);

      columnMappings.forEach(({ consolidatedOrSeparate, period, label, visible }) => {
        if (!visible) return;

        const controlName1 = this.getXbrlControlName(sourceQname, consolidatedOrSeparate, period);
        const controlName2 = this.getXbrlControlName(targetQname, consolidatedOrSeparate, period);

        const control1 = this.statementOfComprehensiveIncomeForm.get(controlName1);
        const control2 = this.statementOfComprehensiveIncomeForm.get(controlName2);

        if (!control1 || !control2) return;

        const value1 = Math.round((parseFloat(control1.value) || 0) * 100) / 100;
        const value2 = Math.round((parseFloat(control2.value) || 0) * 100) / 100;

        if (value1 !== value2) {
          // Add error on source qname
          const sourceErrors = this.validationErrors.get(sourceQname) || [];
          const sourceMsg = `${label}: ${this.getComprehensiveIncomeMismatchMessage(sourceQname)}`;
          if (!sourceErrors.includes(sourceMsg)) {
            sourceErrors.push(sourceMsg);
            this.validationErrors.set(sourceQname, sourceErrors);
          }

          // Add error on target qname
          const targetErrors = this.validationErrors.get(targetQname) || [];
          const targetMsg = `${label}: ${this.getComprehensiveIncomeMismatchMessage(targetQname)}`;
          if (!targetErrors.includes(targetMsg)) {
            targetErrors.push(targetMsg);
            this.validationErrors.set(targetQname, targetErrors);
          }
        }
      });
    });
  }

  /**
   * Get the section-aware mismatch message for ComprehensiveIncome fields.
   * Section is chosen by qname (matches the if/else-if/else arms below):
   * - ifrs-smes:ComprehensiveIncome_2 (MPERS, 2nd occurrence) → "Components of other comprehensive income that will be reclassified to profit or loss, net of tax"
   * - *:ComprehensiveIncome_2 (MFRS, 2nd occurrence) → "Financial assets measured at fair value through other comprehensive income"
   * - ComprehensiveIncome (1st occurrence, either taxonomy) → "Comprehensive income attributable to"
   */
  private getComprehensiveIncomeMismatchMessage(qname: string): string {
    let section: string;
    if (qname === 'ifrs-smes:ComprehensiveIncome_2') {
      section = 'Components of other comprehensive income that will be reclassified to profit or loss, net of tax';
    } else if (qname.endsWith(':ComprehensiveIncome_2')) {
      section = 'Financial assets measured at fair value through other comprehensive income';
    } else {
      section = 'Comprehensive income attributable to';
    }
    return this.equalityValidation.formatFieldMismatch({
      fieldQname: 'Total Comprehensive Income',
      fieldSheetKey: SHEET_KEYS.statementOfComprehensiveIncome,
      taxonomy: this.crossRefService.isMFRS() ? 'MFRS' : 'MPERS',
      section,
      sheetLabelOverride: SOCI_SHEET_LABEL,
    });
  }

  /**
   * True when `msg` is a SAME-SHEET ComprehensiveIncome <-> ComprehensiveIncome_2
   * mismatch produced by getComprehensiveIncomeMismatchMessage — i.e. a mismatch
   * message (`MISMATCH_CORE`) tagged with THIS sheet's label (`SOCI_SHEET_LABEL`).
   * Used by runSameSheetEqualityValidations to drop ONLY its own messages each
   * cycle while preserving extracted-sum warnings and cross-sheet messages to any
   * other sheet. A cross-sheet message references a DIFFERENT sheet label, so it
   * returns false and is never clobbered.
   */
  private isOwnSameSheetMismatch(msg: string): boolean {
    return msg.includes(MISMATCH_CORE) && msg.includes(SOCI_SHEET_LABEL);
  }

  /**
   * Check if a field has equality validation errors
   */
  hasEqualityError(qname: string): boolean {
    return this.equalityValidationErrors.has(qname) &&
      this.equalityValidationErrors.get(qname)!.length > 0;
  }

  /**
   * Get equality validation error message for a field
   */
  getEqualityErrorMessage(qname: string): string {
    const errors = this.equalityValidationErrors.get(qname);
    if (!errors || errors.length === 0) return '';

    return errors.map(err => err.message).join('; ');
  }

  /**
   * Get field value by qname and suffix
   */
  private getFieldValue(qname: string, suffix: string): number {
    const controlName = qname + suffix;
    const control = this.statementOfComprehensiveIncomeForm.get(controlName);

    if (!control) {
      console.warn(`⚠️ Control not found: ${controlName}`);
      return 0;
    }

    const value = control.value;

    // IMPORTANT: Convert to number to prevent string concatenation (e.g., "1" + "1" = "11")
    // parseFloat handles null/undefined by returning NaN, so we use || 0 as fallback
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? 0 : numericValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfComprehensiveIncome);
    const savedData = this.stateService.getSheetData(
      SHEET_KEYS.statementOfComprehensiveIncome
    );

    if (savedData && Object.keys(savedData).length > 0) {
      this.statementOfComprehensiveIncomeForm.patchValue(savedData, {
        emitEvent: false,
      });

      // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status on initial load
      this.statementOfComprehensiveIncomeForm.markAsPristine();

      // CRITICAL: re-pull Role 12's Profit (loss) AFTER patching saved state —
      // the persisted slice may hold a stale copy from an earlier session.
      this.pullRole12Values();

      // Recalculate totals and validate after loading
      setTimeout(() => {
        this.calculateTotals();

        // Save calculated values to state so other sheets (e.g., Role 19) can read
        // the latest calculated totals for cross-sheet validation.
        // calculateTotals() uses emitEvent: false, so setupStatePersistence doesn't fire.
        this.isStateSelfUpdate = true;
        this.stateService.updateSheetData(
          SHEET_KEYS.statementOfComprehensiveIncome,
          this.statementOfComprehensiveIncomeForm.getRawValue(),
          true,
          true // skipEditedMark — this is initialization, not a user edit
        );
        this.isStateSelfUpdate = false;

        this.runAllValidations();

        // Mark ONLY valid controls as pristine (keep invalid controls dirty/touched so errors show)
        Object.keys(this.statementOfComprehensiveIncomeForm.controls).forEach(key => {
          const control = this.statementOfComprehensiveIncomeForm.get(key);
          if (control && !control.invalid) {
            control.markAsPristine();
          }
        });
        this.isLoadingState = false;
        this.isValidationComplete = true;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfComprehensiveIncome);
          // Initial load has settled — from here the state listener stops
          // re-patching so a foreign commit can't clobber in-edit rows.
          this.isInitialLoadComplete = true;
        }, 600);
      }, 100);
    } else {
      // Even with no saved state, Profit (loss) still comes from Role 12.
      this.pullRole12Values();

      // Still calculate totals so calculated fields show 0.00 instead of blank
      setTimeout(() => {
        this.calculateTotals();

        // Save calculated values to state (even with no saved data, calculated defaults matter)
        this.isStateSelfUpdate = true;
        this.stateService.updateSheetData(
          SHEET_KEYS.statementOfComprehensiveIncome,
          this.statementOfComprehensiveIncomeForm.getRawValue(),
          true,
          true
        );
        this.isStateSelfUpdate = false;

        this.isLoadingState = false;
        this.isValidationComplete = true;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfComprehensiveIncome);
          // Initial load has settled — from here the state listener stops
          // re-patching so a foreign commit can't clobber in-edit rows.
          this.isInitialLoadComplete = true;
        }, 600);
      }, 100);
    }
  }

  /**
   * Conditional-required rule for the two `Comprehensive income, attributable
   * to ...` rows.
   *
   * When `filingPrerequisites.NatureOfFinancialStatements` is "Consolidated",
   * every VISIBLE column of both rows must carry a value; an empty cell is a
   * BLOCKING error (red row, "Failed" badge), never a Suggested Change.
   *
   * Schema-driven, not hard-coded: it walks whichever controls declare a
   * `requiredIf` block, so the MFRS and MPERS variants are covered by the same
   * code and a schema edit needs no change here. The pure
   * `role15StatementOfComprehensiveIncomeValidator` reads the SAME block in
   * `checkConditionalRequiredErrors()` — this sheet is on `PURE_VERDICT_SHEETS`,
   * so the sheet-level tick comes from there and the two must agree cell for
   * cell. Keep the visible-column set, the emptiness test, and the message
   * (including the `"<Column> Current|Previous: "` prefix) identical in both.
   *
   * Zero is a reported value — only `null` is missing. A group with no
   * non-controlling interests enters `0`, and that satisfies the rule.
   *
   * _History:_ removed 2026-06-03 (rows made optional), REINSTATED 2026-08-21
   * after the P.O. re-checked the source sheets, for MFRS and MPERS alike.
   */
  private validateConsolidatedRequiredFields(): void {
    if (!this.schema) return;

    this.mandatoryFieldErrors.clear();

    const conditionalControls = this.schema.controls.filter(
      (control) => control.requiredIf && !this.isSectionHeader(control)
    );
    if (conditionalControls.length === 0) return;

    // Visibility comes from the shared dimension-visibility seam via the
    // `showConsolidatedColumns` / `hasPreviousYear` live getters — the same
    // resolver the pure validator calls, so on-screen and validated columns
    // cannot drift.
    const columnsToValidate: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'CurrentYear' | 'PreviousYear';
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'CurrentYear',
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'PreviousYear',
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'CurrentYear',
        label: 'Separate Current',
        show: true, // Always visible
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'PreviousYear',
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    conditionalControls.forEach((control) => {
      const { sheet, field, equals, errorMessage } = control.requiredIf!;
      const qname = control.qname;

      // Resolve the referenced value through the SAME rule the
      // column-visibility resolver uses (`resolvePrerequisiteValue`:
      // prereq-first, then filingInfo) — never a hand-rolled `??` chain.
      //
      // `??` is NOT equivalent here and that difference was a live defect. In
      // Phase 2 the role-0 slot is a stub that `applyDefaultValuesToState`
      // still writes into, so it is a POPULATED object carrying this field as
      // an EMPTY STRING — not `undefined`. `??` only falls through on
      // null/undefined, so it returned `''`, the `!== equals` guard below
      // short-circuited, and neither attribution row ever raised its message on
      // any Phase-2 filing (which is every filing that reaches this sheet).
      // `resolvePrerequisiteValue` treats `''` in the prereq slice as absent
      // and falls through, which is exactly why the rule lives in one place.
      //
      // Role 12 carries the identical `requiredIf` on its own two
      // "Profit (loss), attributable to ..." rows and hit this same defect —
      // see `validateConditionalMandatoryFields()` there.
      const driverValue =
        sheet === SHEET_KEYS.filingPrerequisites
          ? resolvePrerequisiteValue(field, {
              filingPrerequisites: this.stateService.getSheetData(
                SHEET_KEYS.filingPrerequisites
              ),
              filingInfo: this.stateService.getSheetData(SHEET_KEYS.filingInfo),
            })
          : this.stateService.getSheetData(sheet as any)?.[field];
      // --------------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log("[Comprehensive Income Sheet] Control: ", control);
      // console.log("[Comprehensive Income Sheet] Driver Value: ", driverValue);
      // console.log("[Comprehensive Income Sheet] QName: ", qname);
      // console.log("[Comprehensive Income Sheet] Sheet: ", sheet);
      // console.log("[Comprehensive Income Sheet] Equals: ", equals);
      // console.log("[Comprehensive Income Sheet] Field: ", field);
      // console.log("[Comprehensive Income Sheet] Error Message: ", errorMessage);
      // --------------------------
      if (driverValue !== equals) {
        // Condition no longer met — drop any stale mandatory errors for this
        // row while preserving mismatch / cross-sheet errors on the same key.
        this.dropMandatoryErrorsFor(qname);
        return;
      }

      const errors: string[] = [];
      columnsToValidate.forEach(({ consolidatedOrSeparate, period, label, show }) => {
        if (!show) return;

        const controlName = `${qname}[${consolidatedOrSeparate}][${period}]`;
        const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);

        // `=== null` deliberately: 0 is a reported value.
        if (formControl && formControl.value === null) {
          errors.push(`${label}: ${errorMessage}`);
          this.mandatoryFieldErrors.add(qname);
        }
      });

      this.dropMandatoryErrorsFor(qname);
      if (errors.length > 0) {
        const existing = this.validationErrors.get(qname) ?? [];
        this.validationErrors.set(qname, [...existing, ...errors]);
        this.mandatoryErrorMessages.set(qname, errors);
      }
    });
  }

  /**
   * Withdraw this row's conditional-required messages, leaving every other
   * error on the same key intact. Reads the {@link mandatoryErrorMessages}
   * ledger rather than matching on the message copy.
   */
  private dropMandatoryErrorsFor(qname: string): void {
    const ours = this.mandatoryErrorMessages.get(qname);
    this.mandatoryErrorMessages.delete(qname);
    if (!ours?.length) return;

    const existing = this.validationErrors.get(qname);
    if (!existing) return;

    const kept = existing.filter((message) => !ours.includes(message));
    if (kept.length > 0) {
      this.validationErrors.set(qname, kept);
    } else {
      this.validationErrors.delete(qname);
    }
  }

  /** Is this message one the conditional-required rule contributed to `qname`? */
  private isMandatoryFieldError(qname: string, message: string): boolean {
    return this.mandatoryErrorMessages.get(qname)?.includes(message) ?? false;
  }

  /**
   * Check if a specific cell carries the conditional-required error, so the
   * template can paint just that input red rather than the whole row.
   */
  hasConsolidatedRequiredError(controlBaseName: string, suffix: string): boolean {
    if (!this.mandatoryFieldErrors.has(controlBaseName)) return false;

    const columnSuffixes: Record<string, string> = {
      ConsolidatedCurrent: '[Consolidated][CurrentYear]',
      ConsolidatedPrevious: '[Consolidated][PreviousYear]',
      SeparateCurrent: '[Separate][CurrentYear]',
      SeparatePrevious: '[Separate][PreviousYear]',
    };
    const controlName = controlBaseName + (columnSuffixes[suffix] ?? suffix);
    const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);

    // `=== null` deliberately: 0 is a reported value (mirrors the rule above).
    return formControl ? formControl.value === null : false;
  }

  /**
   * Cross-sheet validation: Compare ComprehensiveIncome and OtherComprehensiveIncome values between Role 15 and Role 19
   * Checks if ifrs-full:ComprehensiveIncome and ifrs-full:OtherComprehensiveIncome (or MPERS equivalents) values match
   * between Statement of Comprehensive Income and Statement of Changes in Equity
   * Validates BOTH the first occurrence and the second occurrence (_2 suffix) for both fields
   */
  private validateComprehensiveIncomeAgainstRole19(): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍🔍🔍 [Role 15 → Role 19] validateComprehensiveIncomeAgainstRole19 CALLED - TIMESTAMP:', new Date().toISOString());

    if (!this.schema) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⚠️ [Role 15 → Role 19] Schema not loaded - returning early');
      return;
    }

    // Get data from Role 19 (Statement of Changes in Equity).
    // Cross-sheet recompute parity: read SOCIE from the recomputed bag
    // built by runEqualityValidations() (which runs immediately before this in
    // runAllValidations()), so OCI compares against RECOMPUTED SOCIE totals — not
    // the stale persisted extraction. Fall back to the persisted slice only if
    // the bag has not been built yet (defensive; should not happen in the normal
    // runAllValidations sequence).
    // Use empty object as fallback — when the API returns all-null values for SOCIE,
    // the transformer produces an empty object. We still need to validate because
    // Role 15 may have non-zero ComprehensiveIncome that should mismatch with 0.
    const role19Data =
      this.lastRecomputedSheetBag?.[SHEET_KEYS.statementOfChangesInEquity] ||
      this.stateService.getSheetData(SHEET_KEYS.statementOfChangesInEquity) ||
      {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 [Role 15 → Role 19] Starting cross-sheet validation for ComprehensiveIncome and OtherComprehensiveIncome');

    // Determine if MFRS or MPERS
    const isMFRS = this.crossRefService.isMFRS();
    const comprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome';
    const otherComprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:OtherComprehensiveIncome'
      : 'ifrs-smes:OtherComprehensiveIncome';

    // Validate ComprehensiveIncome and OtherComprehensiveIncome against Role 19 (SOCIE)
    // Note: _2 variants (e.g. ComprehensiveIncome_2) are same-sheet validations only,
    // they should NOT be cross-validated against SOCIE
    const qnamesToValidate = [
      comprehensiveIncomeQname,
      otherComprehensiveIncomeQname,
    ];

    // Check all 4 columns
    const columns = [
      { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
      { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
      { suffix: '[Separate][CurrentYear]', label: 'Separate Current' },
      { suffix: '[Separate][PreviousYear]', label: 'Separate Previous' },
    ];

    // Loop through all qnames (ComprehensiveIncome and OtherComprehensiveIncome, both occurrences)
    qnamesToValidate.forEach(currentQname => {
      const errors: string[] = [];
      const isSecondOccurrence = currentQname.includes('_2');

      // Get the base qname without _2 suffix
      const baseQname = currentQname.replace('_2', '');

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 [Role 15 → Role 19] Validating ${isSecondOccurrence ? 'SECOND' : 'FIRST'} occurrence: ${currentQname} (base: ${baseQname})`);

      columns.forEach(({ suffix, label }) => {
        const role15ControlName = currentQname + suffix;
        const role15Control = this.statementOfComprehensiveIncomeForm.get(role15ControlName);

        if (!role15Control) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⚠️ [Role 15 → Role 19] Control not found in form: ${role15ControlName}`);
          return;
        }

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`🔍 [Role 15 → Role 19] Processing column: ${label} (${role15ControlName})`);

        // Get Role 15 value (use getRawValue to handle disabled controls)
        const allRole15Values = this.statementOfComprehensiveIncomeForm.getRawValue();
        const role15Value = allRole15Values[role15ControlName];

        // Role 19 uses equity column format for MFRS/MPERS
        // Try multiple possible control name formats from Role 19
        const consolidationType = suffix.includes('Consolidated') ? 'Consolidated' : 'Separate';
        const periodType = suffix.includes('Current') ? 'Current' : 'Previous';

        // Use the BASE qname (without _2 suffix) for Role 19 lookups
        // Role 19 uses bracket format: {rowQname}:{columnQname}[Consolidated][CurrentYear]
        const periodKey = periodType === 'Current' ? 'CurrentYear' : 'PreviousYear';
        const possibleRole19ControlNames = [
          // Equity column bracket format: {qname}:{columnQname}[Consolidated][CurrentYear]
          `${baseQname}:${isMFRS ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember'}[${consolidationType}][${periodKey}]`,

          // Try with EquityAttributableToOwnersOfParentMember
          `${baseQname}:${isMFRS ? 'ifrs-full:EquityAttributableToOwnersOfParentMember' : 'ifrs-smes:EquityAttributableToOwnersOfParentMember'}[${consolidationType}][${periodKey}]`,

          // Legacy dual column format: {qname}{suffix}
          role15ControlName,
        ];

      let role19Value = null;
      let foundControlName = '';

      // Try to find the matching control in Role 19 data
      // Accept null/0 values — they are valid for calculated fields
      for (const controlName of possibleRole19ControlNames) {
        if (role19Data[controlName] !== undefined) {
          role19Value = role19Data[controlName];
          foundControlName = controlName;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ [Role 15 → Role 19] Found match: ${foundControlName} = ${role19Value}`);
          break;
        }
      }

      // Skip if BOTH values are null/undefined/empty (no data on either side)
      const role15Empty = role15Value === null || role15Value === undefined || role15Value === '';
      const role19Empty = !foundControlName || (role19Value === null || role19Value === undefined || role19Value === '');
      if (role15Empty && role19Empty) {
        return;
      }

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 [Role 15 → Role 19] Comparing ${label}:`, {
      //   role15Value,
      //   role19Value,
      //   role15ControlName,
      //   foundControlName
      // });

      // Convert to numbers for comparison (treat null/empty as 0 for calculated fields)
      const role15Num = role15Empty ? 0 : parseFloat(role15Value);
      const role19Num = role19Empty ? 0 : parseFloat(role19Value);

      // Round to cents, flag any difference (≥ 0.01) — a one-cent mismatch must
      // be reported (raw `> 0.01` skipped rounding and needed ≥ 0.02).
      if (differsAtCentPrecision(role15Num, role19Num)) {
        // Get the field label from schema using the BASE qname
        const fieldControl = this.schema?.controls.find(control => control.qname === baseQname);
        const fieldLabel = fieldControl?.label || baseQname.split(':')[1] || 'Comprehensive Income';

        const taxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';
        errors.push(
          this.equalityValidation.formatFieldMismatch({
            columnLabel: label,
            fieldQname: fieldLabel,
            fieldSheetKey: SHEET_KEYS.statementOfChangesInEquity,
            taxonomy,
            sheetLabelOverride: SOCIE_SHEET_LABEL,
          })
        );

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(
        //   `⚠️ Cross-sheet mismatch for ${currentQname} [${label}]:`,
        //   {
        //     role15Value: role15Num,
        //     role19Value: role19Num,
        //     role19ControlName: foundControlName,
        //     diff: Math.abs(role15Num - role19Num)
        //   }
        // );
      } else {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(
        //   `✅ Cross-sheet values MATCH for ${currentQname} [${label}]:`,
        //   {
        //     role15Value: role15Num,
        //     role19Value: role19Num,
        //     diff: Math.abs(role15Num - role19Num)
        //   }
        // );
      }
      }); // End of columns.forEach

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 [Role 15 → Role 19] Validation complete for ${currentQname}. Total errors found: ${errors.length}`);
      // console.log(`🔍 [Role 15 → Role 19] Current Map state BEFORE adding errors:`, {
      //   hasKey: this.validationErrors.has(currentQname),
      //   existingErrors: this.validationErrors.get(currentQname)
      // });

      // Add or update validation errors in the Map
      if (errors.length > 0) {
        const existingErrors = this.validationErrors.get(currentQname) || [];

        // Filter out old cross-sheet errors
        const nonCrossSheetErrors = existingErrors.filter(
          error => !error.includes(SOCIE_SHEET_LABEL)
        );

        // Combine with new cross-sheet errors
        const combinedErrors = [...nonCrossSheetErrors, ...errors];

        this.validationErrors.set(currentQname, combinedErrors);
        
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⚠️ [Role 15 → Role 19] Cross-sheet validation FAILED for ${currentQname} - ${errors.length} error(s) added to validationErrors Map`);
        // console.log(`⚠️ [Role 15 → Role 19] Errors:`, errors);
        // console.log(`⚠️ [Role 15 → Role 19] Combined errors:`, combinedErrors);
        // console.log(`⚠️ [Role 15 → Role 19] Total errors for ${currentQname}:`, combinedErrors.length);
        // console.log(`⚠️ [Role 15 → Role 19] Map state AFTER setting:`, {
        //   hasKey: this.validationErrors.has(currentQname),
        //   storedErrors: this.validationErrors.get(currentQname)
        // });
      } else {
        // Remove cross-sheet errors if they exist, keep other errors
        const existingErrors = this.validationErrors.get(currentQname);
        if (existingErrors && existingErrors.length > 0) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`🔄 [Role 15 → Role 19] Existing errors before filtering:`, existingErrors);

          const nonCrossSheetErrors = existingErrors.filter(
            error => !error.includes(SOCIE_SHEET_LABEL)
          );

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`🔄 [Role 15 → Role 19] Non-cross-sheet errors after filtering:`, nonCrossSheetErrors);

          if (nonCrossSheetErrors.length > 0) {
            this.validationErrors.set(currentQname, nonCrossSheetErrors);
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`✅ [Role 15 → Role 19] Kept ${nonCrossSheetErrors.length} non-cross-sheet error(s)`);
          } else {
            this.validationErrors.delete(currentQname);
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`✅ [Role 15 → Role 19] Deleted all errors for ${currentQname} from Map`);
          }
        } else {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ [Role 15 → Role 19] Cross-sheet validation PASSED - no existing errors to remove for ${currentQname}`);
        }
      }
    }); // End of qnamesToValidate.forEach

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ [Role 15 → Role 19] Cross-sheet validation complete for both occurrences`);
  }

  /**
   * Validate decimal format for all fields
   * Checks that values (if not null) are decimal with up to 2 decimal places
   */
  private validateDecimalFormat(): void {
    if (!this.schema) return;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Validating decimal format for all fields');

    this.schema.controls.forEach((control, index) => {
      // Skip headers
      if (this.isSectionHeader(control)) return;

      // Only validate fields with dualColumn (monetary fields)
      if (control.ui.dualColumn) {
        const controlBaseName = this.getControlBaseName(control, index);

        // Check all 4 columns
        const suffixes = [
          'ConsolidatedCurrent',
          'ConsolidatedPrevious',
          'SeparateCurrent',
          'SeparatePrevious',
        ];

        suffixes.forEach((suffix) => {
          const controlName = controlBaseName + suffix;
          const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);

          if (formControl) {
            const value = formControl.value;

            // Allow null, but if has value, validate decimal format
            if (value !== null && value !== undefined && value !== '') {
              const numValue = parseFloat(value);

              // Check if it's a valid number
              if (isNaN(numValue)) {
                // Mark as dirty and touched to show error
                formControl.markAsDirty();
                formControl.markAsTouched();

                // Set custom error for invalid decimal
                formControl.setErrors({
                  ...formControl.errors,
                  invalidDecimal: true
                });

                // Debug Log (Only enable when required to debug, do not allow this to land into prod)
                // console.log(`⚠️ Invalid decimal value: ${controlName} = ${value}`);
              } else {
                // Check decimal places (up to 2 decimal places)
                const decimalPlaces = (value.toString().split('.')[1] || '').length;

                if (decimalPlaces > 2) {
                  // Mark as dirty and touched to show error
                  formControl.markAsDirty();
                  formControl.markAsTouched();

                  // Set custom error for too many decimal places
                  formControl.setErrors({
                    ...formControl.errors,
                    invalidDecimal: true
                  });

                  // Debug Log (Only enable when required to debug, do not allow this to land into prod)
                  // console.log(`⚠️ Too many decimal places: ${controlName} = ${value} (${decimalPlaces} dp)`);
                } else {
                  // Clear invalidDecimal error if value is valid (preserve other errors)
                  if (formControl.hasError('invalidDecimal')) {
                    const errors = { ...formControl.errors };
                    delete errors['invalidDecimal'];
                    formControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
                  }
                }
              }
            } else {
              // Value is null/undefined/empty - clear ONLY invalidDecimal error (preserve other errors like consolidatedRequired)
              if (formControl.hasError('invalidDecimal')) {
                const errors = { ...formControl.errors };
                delete errors['invalidDecimal'];
                // IMPORTANT: Don't clear other errors, only remove invalidDecimal
                formControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
              }
            }
          }
        });
      }
    });
  }

  /**
   * Setup listener for external state updates (e.g., when backend API data is populated)
   * Skips re-validation when the state change was self-triggered by setupStatePersistence.
   */
  private setupStateListener(): void {
    this.stateService.modifiedState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        // Skip re-validation when this component triggered the state update itself
        // (setupStatePersistence already ran all validations via setupCalculations)
        if (this.isStateSelfUpdate) {
          return;
        }

        // After initial load completes, skip re-patching. Past this point the
        // only emissions on this slice are commits — this sheet's own (already
        // dropped by `isStateSelfUpdate` above) and, via
        // `FilingFormStateService.applyRemoteChange`, a sibling tab's. A
        // re-patch here would overwrite the typed-but-unsaved values of rows
        // the user has OPEN (inline edit-mode sheets commit on Tick, so those
        // edits live only in the form while state still holds the pre-edit
        // number). Adopting a sibling's values is `adoptRemoteState()`'s job —
        // it runs later, on `remoteUpdateApplied$`, and skips open rows.
        // Mirrors role23/role24. See isInitialLoadComplete.
        if (this.isInitialLoadComplete) {
          return;
        }

        const newData = state.statementOfComprehensiveIncome;

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(
        //   '📡 State listener fired in role15 (statement-of-comprehensive-income)'
        // );
        // console.log('📡 newData exists?', !!newData);
        // console.log(
        //   '📡 newData keys:',
        //   newData ? Object.keys(newData).length : 0
        // );

        // Only reload if we have new data
        if (newData && Object.keys(newData).length > 0) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('📡 Reloading form with new data from state service');
          // console.log(
          //   '📡 Sample of newData:',
          //   Object.entries(newData).slice(0, 5)
          // );

          // console.log('⏳ About to call patchValue from state listener');
          this.statementOfComprehensiveIncomeForm.patchValue(newData, {
            emitEvent: false,
          });
          // console.log('✔️ patchValue from state listener completed');

          // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status
          this.statementOfComprehensiveIncomeForm.markAsPristine();

          // Recalculate totals and validate after loading
          setTimeout(() => {
            this.calculateTotals();
            this.runAllValidations();

            // Mark ONLY valid controls as pristine (keep invalid controls dirty/touched so errors show)
            Object.keys(this.statementOfComprehensiveIncomeForm.controls).forEach(key => {
              const control = this.statementOfComprehensiveIncomeForm.get(key);
              if (control && !control.invalid) {
                control.markAsPristine();
              }
            });
          }, 100);
        }
      });

    // Listen for Role 19 (Statement of Changes in Equity) data changes only
    // (not Role 15's own saves, not other unrelated sheets).
    //
    // Three-layer guard against spurious init-time firings — mirrors the
    // pattern used in role19-610000-statement-of-changes-in-equity.component.ts:
    //   1. filter(!isSheetInitializing) — drops emissions produced by Role 15's
    //      own loadSavedState → updateSheetData chain while the sheet is still
    //      initializing.
    //   2. distinctUntilChanged on the r19 hash — Role 15's self-updates leave
    //      statementOfChangesInEquity untouched (shallow spread in
    //      updateSheetData), so the hash is byte-identical and gets filtered out.
    //   3. skip(1) AFTER distinctUntilChanged — drops the first replayed
    //      post-init distinct value (distinctUntilChanged always lets its
    //      first-ever value through because it has no prior value to compare
    //      against).
    //
    // Only real changes to Role 19 data reach the subscribe block.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfComprehensiveIncome)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r19: state.statementOfChangesInEquity,
        })),
        distinctUntilChanged(),
        skip(1),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Only cross-sheet revalidation — same-sheet user edits must not be disturbed
        this.validateCrossSheet();
        this.evaluateSheetStatus();

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('📡 Role 0/19 data changed - re-validating cross-sheet in Role 15');
      });

    // ----------------------------------
    // Cross-tab safety net (mirrors role-19 pattern). When filingPrerequisites
    // or statementOfChangesInEquity is mutated remotely (sibling tab edits),
    // re-run validations so the cross-sheet equity reconciliation indicators
    // stay accurate even when the user is sitting on this sheet at the time
    // of the remote update. Idempotent with the modifiedState$ watch above —
    // duplicate fires are harmless because runAllValidations is pure.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfComprehensiveIncome)),
        filter(() => !!this.schema && !!this.statementOfComprehensiveIncomeForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role15-statementOfComprehensiveIncome',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.statementOfComprehensiveIncome))
          this.adoptRemoteState();

        this.validateCrossSheet();
        this.evaluateSheetStatus();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption for role15-410000 (SoCI). Rows currently open in edit
   * mode are skipped — the user's in-flight work wins until they tick (local
   * overwrites and broadcasts) or press X (the row lands on the adopted
   * value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.statementOfComprehensiveIncomeForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfComprehensiveIncome),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: FieldControl, schemaIndex: number): boolean {
    const controlBaseName = this.getControlBaseName(control, schemaIndex);
    return this.editingRows.has(controlBaseName);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls
   */
  enterEditMode(control: FieldControl, schemaIndex: number): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    const controlBaseName = this.getControlBaseName(control, schemaIndex);

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(controlBaseName, this.getRowValidationStatus(control, schemaIndex));

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(controlBaseName, 'Consolidated', 'Current'),
      this.getXbrlControlName(controlBaseName, 'Consolidated', 'Previous'),
      this.getXbrlControlName(controlBaseName, 'Separate', 'Current'),
      this.getXbrlControlName(controlBaseName, 'Separate', 'Previous'),
    ];

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach((name) => {
      originalValues[name] =
        this.statementOfComprehensiveIncomeForm.get(name)?.value ?? null;
    });
    this.editingRows.set(controlBaseName, originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.statementOfComprehensiveIncome);

    // Enable all 4 controls, revealing only the columns this permutation SHOWS —
    // a hidden required column is permanently null, so enabling makes it invalid
    // and revealing it would paint the row red with no text to explain it.
    enableDualColumnRowAndRevealVisible(
      this.statementOfComprehensiveIncomeForm,
      controlBaseName,
      this.dimensionVisibility,
      { emitEvent: true }
    );
  }

  /**
   * Save edit mode (Tick) — disable controls, subset-persist, calc, same+cross validate,
   * reconcile edited flag, clear editing, publish status.
   */
  async saveEditMode(control: FieldControl, schemaIndex: number): Promise<void> {
    const controlBaseName = this.getControlBaseName(control, schemaIndex);

    if (!this.editingRows.has(controlBaseName)) {
      return;
    }

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(controlBaseName, 'Consolidated', 'Current'),
      this.getXbrlControlName(controlBaseName, 'Consolidated', 'Previous'),
      this.getXbrlControlName(controlBaseName, 'Separate', 'Current'),
      this.getXbrlControlName(controlBaseName, 'Separate', 'Previous'),
    ];

    controlNames.forEach((name) => {
      this.statementOfComprehensiveIncomeForm.get(name)?.disable({ emitEvent: false });
    });
    this.frozenRowStatus.delete(controlBaseName);
    this.editingRows.delete(controlBaseName);

    // Recalculate totals FIRST so the persisted subset includes the freshly
    // recalculated calculated/derived totals (downstream sheets read these).
    this.calculateTotals();

    const subset = pickSubset(this.statementOfComprehensiveIncomeForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.isStateSelfUpdate = true;
    this.stateService.updateSheetData(
      SHEET_KEYS.statementOfComprehensiveIncome,
      subset,
      true
    );
    this.isStateSelfUpdate = false;

    this.autoCheckComprehensiveIncomeMatch();
    this.validateSameSheet();
    this.validateCrossSheet();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfComprehensiveIncome);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfComprehensiveIncome);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to the snapshot captured on enterEditMode,
   * then disable controls. "Edited" status is derived by MappingStatusService,
   * so no per-row marker needs to be managed here.
   */
  cancelEditMode(control: FieldControl, schemaIndex: number): void {
    const controlBaseName = this.getControlBaseName(control, schemaIndex);
    const originalValues = this.editingRows.get(controlBaseName);
    if (!originalValues) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfComprehensiveIncome);
    Object.keys(originalValues).forEach((name) => {
      const formControl = this.statementOfComprehensiveIncomeForm.get(name);
      if (formControl) {
        const revertValue = name in adopted
          ? adopted[name]
          : (originalValues[name] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(controlBaseName);
    this.editingRows.delete(controlBaseName);

    this.calculateTotals();
    this.autoCheckComprehensiveIncomeMatch();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfComprehensiveIncome);
    this.evaluateSheetStatus();
  }

  /**
   * Get validation status for a row (checks all 4 columns).
   * Delegates to MappingStatusService.computeRowStatus for Edited/Validated* resolution;
   * preserves all existing Failed-branch inputs (form invalidity, mandatory, equality errors).
   * Handles both regular qnames and occurrence suffixes.
   */
  getRowValidationStatus(
    control: FieldControl,
    schemaIndex?: number
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    // Only check monetary items
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number')
      return 'Validated';

    // Get the actual control base name (handles duplicates with occurrence suffix)
    const controlBaseName = schemaIndex !== undefined
      ? this.getControlBaseName(control, schemaIndex)
      : control.qname;

    if (this.isRowInEditMode(control, schemaIndex !== undefined ? schemaIndex : 0)) {
      const frozen = this.frozenRowStatus.get(controlBaseName);
      if (frozen !== undefined) return frozen;
    }

    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const controlNames = [
      `${controlBaseName}[Consolidated][CurrentYear]`,
      `${controlBaseName}[Consolidated][PreviousYear]`,
      `${controlBaseName}[Separate][CurrentYear]`,
      `${controlBaseName}[Separate][PreviousYear]`,
    ];

    // Gather current values
    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);
      if (formControl) {
        currentValues[controlName] = formControl.value;
      }
    }

    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control, schemaIndex);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(SHEET_KEYS.statementOfComprehensiveIncome),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // Calculated totals are computed client-side (calculateTotals) and are absent
      // from the persisted baseline, so the deviation check would mark them Edited
      // with zero user edits. They are never user-editable (enterEditMode skips
      // them) — mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags (full logic).
   * This is the single definition of "does the row have a hard error" vs "only a
   * suggested-change warning" — consumed by both the Mapping Status (which layers
   * the Edited override on top) and the row colour intent (which deliberately
   * ignores edit mode). Keeping it in one place means the red/yellow background
   * can never disagree with the Failed/Validated* badge.
   *
   * Extracted VERBATIM from getRowValidationStatus — same inputs (form
   * invalidity, mandatory-but-empty, visible non-extracted-sum validation errors
   * for hasError; visible extracted-sum mismatches for the warning), so the
   * badge behaviour is unchanged.
   */
  private _computeRowErrorFlags(
    control: FieldControl,
    schemaIndex?: number
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    // Get the actual control base name (handles duplicates with occurrence suffix)
    const controlBaseName = schemaIndex !== undefined
      ? this.getControlBaseName(control, schemaIndex)
      : control.qname;

    const controlNames = [
      `${controlBaseName}[Consolidated][CurrentYear]`,
      `${controlBaseName}[Consolidated][PreviousYear]`,
      `${controlBaseName}[Separate][CurrentYear]`,
      `${controlBaseName}[Separate][PreviousYear]`,
    ];

    // Only a VISIBLE column may paint the row. A column this permutation does not
    // render can never SHOW a message — every message surface on this sheet
    // filters hidden columns out — so counting one here produces a red row with an
    // empty Error Description. That is the UAT defect of 2026-08-26 (role 18's
    // "Cash and cash equivalents at beginning / end of period" rows, opened on a
    // filing with no previous year): `enable()` makes a hidden, permanently-null
    // required cell invalid, and the reveal on row-open then marks it touched.
    //
    // Developer Note (TODO): `hasValue` below is deliberately still read across
    // ALL FOUR columns. Narrowing it to the visible ones is a separate, correct
    // change — a mandatory row whose only value sits in a hidden column IS
    // unsatisfied — but it can only ADD red, so it must not land until this
    // sheet's required-message builder is confirmed to emit a per-visible-column
    // mandatory line for that case. Otherwise it recreates the very defect above,
    // in the other direction. Role 18 DOES narrow both halves — its
    // `validateRequiredFields()` was made the last writer of the per-column
    // mandatory partition on 2026-08-26, so its red is always accompanied by
    // text; that confirmation is what this sheet still owes.
    const columnVisible: boolean[] = [
      this.showConsolidatedColumns,
      this.showConsolidatedColumns && this.hasPreviousYear,
      true,
      this.hasPreviousYear,
    ];

    let hasInvalid = false;
    let hasValue = false;
    controlNames.forEach((controlName, index) => {
      const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);
      if (formControl) {
        if (columnVisible[index] && formControl.invalid && (formControl.dirty || formControl.touched)) hasInvalid = true;
        if (formControl.value !== null && formControl.value !== undefined && formControl.value !== '') hasValue = true;
      }
    });

    // `isMandatoryForValidation` — NOT `isControlMandatory`. This sheet is THE
    // worked example in that function's Developer Note: it sits on
    // PURE_VERDICT_SHEETS, and its pure validator enforces only the two
    // attribution rows' `requiredIf` — NOT the three calculated totals the
    // PO's list flags mandatory. Flag one of those here and the row would paint
    // red with a "Failed" badge while the SHEET tick stayed green and submit
    // stayed open — editor and headless disagreeing on the same data. The
    // computed-control skip inside the predicate is what prevents it. Read that
    // note before changing this line.
    //
    // Deliberately called with NO MandatoryContext: the two conditionally
    // required rows are enforced PER VISIBLE COLUMN by
    // `validateConsolidatedRequiredFields()`, whose messages already reach the
    // `visibleErrors` clause below. Passing a context here would ask a coarser
    // question — "is ANY of the four cells filled?" — and would let a row with
    // three empty mandatory columns read as satisfied.
    const isRequired = isMandatoryForValidation(control as any);

    // Validation errors are column-prefixed (e.g. "Consolidated Current: …"). Mismatches in
    // columns hidden by visibility must NOT contribute to the badge — separate-only filings
    // hide the Consolidated columns; first-year filings hide the Previous-year columns. Filter
    // to the currently visible columns (mirrors getRowErrorDescription's visibility logic).
    const visibleColumnLabels: string[] = [];
    if (this.showConsolidatedColumns) {
      visibleColumnLabels.push('Consolidated Current');
      if (this.hasPreviousYear) visibleColumnLabels.push('Consolidated Previous');
    }
    visibleColumnLabels.push('Separate Current');
    if (this.hasPreviousYear) visibleColumnLabels.push('Separate Previous');

    const allErrors = this.validationErrors.get(control.qname) || [];
    const visibleErrors = allErrors.filter((message) =>
      visibleColumnLabels.some((label) => message.startsWith(label + ':'))
    );

    const hasError =
      hasInvalid ||
      (isRequired && !hasValue) ||
      visibleErrors.some((message) => !message.includes('does not match the extracted sum'));

    // Suggested-Change warning: a visible column has an extracted-sum mismatch.
    const hasSuggestedChangeWarning = visibleErrors.some((message) =>
      message.includes('does not match the extracted sum')
    );

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent: 'error' (red) when the row has a
   * hard error, 'warning' (yellow) when it only has a suggested-change warning,
   * 'none' otherwise. Red outranks yellow. Unlike the Mapping Status this does
   * NOT consult edit mode, so clicking the edit pencil never repaints the row.
   * Mirrors the status guard (number rows only).
   */
  private _computeRowColorIntent(control: FieldControl, schemaIndex?: number): 'error' | 'warning' | 'none' {
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number') return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control, schemaIndex);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Row background colour intent for a row. 'error' → red (`row-has-error`),
   * 'warning' → yellow (`row-has-validation-error`), 'none' → no highlight.
   * Drives the row background INDEPENDENTLY of the Mapping Status so entering
   * edit mode (which forces the status to 'Edited') never repaints the row.
   * Role 15 keeps no row-status cache, so this computes directly.
   */
  getRowColorIntent(control: FieldControl, schemaIndex?: number): 'error' | 'warning' | 'none' {
    return this._computeRowColorIntent(control, schemaIndex);
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   * Handles both regular qnames and occurrence suffixes
   */
  getRowErrorDescription(control: FieldControl, schemaIndex?: number): string {
    // Only check monetary items
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number')
      return '';

    // Get the actual control base name (handles duplicates with occurrence suffix)
    const controlBaseName = schemaIndex !== undefined
      ? this.getControlBaseName(control, schemaIndex)
      : control.qname;

    const errors: string[] = [];           // Red - Error category
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // Determine which columns are visible
    const visibleColumnLabels: string[] = [];
    if (this.showConsolidatedColumns) {
      visibleColumnLabels.push('Consolidated Current');
      if (this.hasPreviousYear) {
        visibleColumnLabels.push('Consolidated Previous');
      }
    }
    visibleColumnLabels.push('Separate Current');
    if (this.hasPreviousYear) {
      visibleColumnLabels.push('Separate Previous');
    }

    // 1. Process validation errors from validationErrors Map
    if (this.validationErrors.has(control.qname)) {
      const validationErrorMessages = this.validationErrors.get(control.qname) || [];

      validationErrorMessages.forEach((errorMsg) => {
        // Check if error is for a visible column
        // Supports both "Label: message" and "[Label] message" formats
        const isVisible = visibleColumnLabels.some(label =>
          errorMsg.startsWith(label + ':') || errorMsg.startsWith('[' + label + ']')
        );
        if (!isVisible) return;

        // Categorize based on error message content
        if (errorMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      });
    }

    // 2. Check for form control errors -> Error category
    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const columns: Array<{ consolidatedOrSeparate: string; period: string; label: string; visible: boolean }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'CurrentYear', label: 'Consolidated Current', visible: this.showConsolidatedColumns },
      { consolidatedOrSeparate: 'Consolidated', period: 'PreviousYear', label: 'Consolidated Previous', visible: this.showConsolidatedColumns && this.hasPreviousYear },
      { consolidatedOrSeparate: 'Separate', period: 'CurrentYear', label: 'Separate Current', visible: true },
      { consolidatedOrSeparate: 'Separate', period: 'PreviousYear', label: 'Separate Previous', visible: this.hasPreviousYear },
    ];

    for (const { consolidatedOrSeparate, period, label, visible } of columns) {
      if (!visible) continue;

      const controlName = `${controlBaseName}[${consolidatedOrSeparate}][${period}]`;
      const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);

      // Use formControl.errors instead of formControl.invalid because
      // disabled (calculated) controls always have status 'DISABLED', never 'INVALID',
      // even when errors are set via setErrors()
      if (formControl && formControl.errors && (formControl.dirty || formControl.touched)) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required'])
            errorMessages.push('Required field');
          if (formControl.errors['min'])
            errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max'])
            errorMessages.push(`Max: ${formControl.errors['max'].max}`);
          if (formControl.errors['comprehensiveIncomeMismatch'])
            errorMessages.push(this.getComprehensiveIncomeMismatchMessage(controlBaseName));
          if (formControl.errors['invalidDecimal'])
            errorMessages.push('Please enter a numeric value with up to 2 decimal places only.');
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (all 4 columns + concat keys).
    if (this.baselineDebug.enabled()) {
      const columns: { key: string; label: string }[] = [
        { key: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
        { key: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
        { key: '[Separate][CurrentYear]', label: 'Separate Current' },
        { key: '[Separate][PreviousYear]', label: 'Separate Previous' },
      ];
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfComprehensiveIncome,
        control as unknown as SummableControl,
        columns,
        (qname, key) => qname + key
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: form control errors
   */
  hasErrorCategory(control: FieldControl, schemaIndex?: number): boolean {
    const controlBaseName = schemaIndex !== undefined
      ? this.getControlBaseName(control, schemaIndex)
      : control.qname;

    const columns: Array<{ consolidatedOrSeparate: 'Consolidated' | 'Separate'; period: 'Current' | 'Previous'; visible: boolean }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', visible: this.showConsolidatedColumns },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', visible: this.showConsolidatedColumns && this.hasPreviousYear },
      { consolidatedOrSeparate: 'Separate', period: 'Current', visible: true },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', visible: this.hasPreviousYear },
    ];

    for (const { consolidatedOrSeparate, period, visible } of columns) {
      if (!visible) continue;

      const controlName = this.getXbrlControlName(controlBaseName, consolidatedOrSeparate, period);
      const formControl = this.statementOfComprehensiveIncomeForm.get(controlName);

      // Use formControl.errors instead of formControl.invalid for disabled (calculated) controls
      if (formControl && formControl.errors && (formControl.dirty || formControl.touched)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings
   */
  hasSuggestedChangeCategory(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    // Determine visible columns
    const visibleColumnLabels: string[] = [];
    if (this.showConsolidatedColumns) {
      visibleColumnLabels.push('Consolidated Current');
      if (this.hasPreviousYear) {
        visibleColumnLabels.push('Consolidated Previous');
      }
    }
    visibleColumnLabels.push('Separate Current');
    if (this.hasPreviousYear) {
      visibleColumnLabels.push('Separate Previous');
    }

    return allErrors.some((errorMsg) => {
      const isVisible = visibleColumnLabels.some(label => errorMsg.startsWith(label + ':'));
      if (!isVisible) return false;

      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if row has validation error (for yellow highlighting - Validated* status)
   * Only returns true for mismatch errors in VISIBLE columns, NOT for consolidated required errors or cross-sheet errors
   */
  hasRowValidationError(control: FieldControl): boolean {
    // A missing mandatory value is blocking — the row must go RED via
    // hasRowFailed, never yellow. Short-circuit before the message filter so a
    // row carrying BOTH a required error and an extracted-sum mismatch still
    // reads as Failed rather than Validated*.
    if (this.mandatoryFieldErrors.has(control.qname)) {
      return false;
    }

    if (!this.validationErrors.has(control.qname)) {
      return false;
    }

    const errors = this.validationErrors.get(control.qname) || [];

    // Determine which columns are visible
    const visibleColumnLabels: string[] = [];
    if (this.showConsolidatedColumns) {
      visibleColumnLabels.push('Consolidated Current');
      if (this.hasPreviousYear) {
        visibleColumnLabels.push('Consolidated Previous');
      }
    }
    visibleColumnLabels.push('Separate Current');
    if (this.hasPreviousYear) {
      visibleColumnLabels.push('Separate Previous');
    }

    // Filter errors to only those for visible columns
    const visibleErrors = errors.filter(error => {
      return visibleColumnLabels.some(label => error.startsWith(label + ':'));
    });

    // Check if any visible error is a cross-sheet validation error
    const hasCrossSheetError = visibleErrors.some(error =>
      error.includes(SOCIE_SHEET_LABEL) || error.includes(SOCI_SHEET_LABEL)
    );

    // Conditional-required errors are blocking too. The set check above already
    // covers the common case; this also catches a required message left on a
    // row whose qname is no longer in the set (e.g. a hidden column).
    const hasRequiredError = visibleErrors.some((error) =>
      this.isMandatoryFieldError(control.qname, error)
    );

    // Only return true if there are visible errors AND they are NOT cross-sheet
    // or conditional-required errors. This means only mismatch errors
    // (ExtractedSum vs CalculatedSum) get the yellow background (Validated*).
    return visibleErrors.length > 0 && !hasCrossSheetError && !hasRequiredError;
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl, schemaIndex?: number): boolean {
    return this.getRowValidationStatus(control, schemaIndex) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (let controlIndex = 0; controlIndex < this.schema.controls.length; controlIndex++) {
      if (this.getRowValidationStatus(this.schema.controls[controlIndex], controlIndex) === 'Failed') {
        hasErrors = true;
        break;
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementOfComprehensiveIncome, { hasErrors });
  }

  /**
   * Check if a specific field has a cross-sheet validation error
   * Used to add red border to individual input fields for cross-sheet mismatches
   * ONLY applies to the Total ComprehensiveIncome field (not attribution fields)
   */
  hasCrossSheetError(controlBaseName: string, suffix: string): boolean {
    const qname = controlBaseName;

    // Determine if MFRS or MPERS
    const isMFRS = this.crossRefService.isMFRS();
    const comprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome';
    const otherComprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:OtherComprehensiveIncome'
      : 'ifrs-smes:OtherComprehensiveIncome';

    // Check for BOTH fields AND BOTH occurrences (_2 suffix)
    const isComprehensiveIncomeFirst = qname === comprehensiveIncomeQname;
    const isComprehensiveIncomeSecond = qname === comprehensiveIncomeQname + '_2';
    const isOtherComprehensiveIncomeFirst = qname === otherComprehensiveIncomeQname;
    const isOtherComprehensiveIncomeSecond = qname === otherComprehensiveIncomeQname + '_2';

    // ONLY check for ComprehensiveIncome and OtherComprehensiveIncome fields (both occurrences each)
    if (!isComprehensiveIncomeFirst && !isComprehensiveIncomeSecond &&
        !isOtherComprehensiveIncomeFirst && !isOtherComprehensiveIncomeSecond) {
      return false;
    }

    // Check if this qname has validation errors
    if (!this.validationErrors.has(qname)) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 hasCrossSheetError [${suffix}]: No validation errors in Map for ${qname}`);
      return false;
    }

    const errors = this.validationErrors.get(qname) || [];
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 hasCrossSheetError [${suffix}]: Found ${errors.length} error(s) in Map:`, errors);

    // Check if there are cross-sheet errors (errors that mention "Statement of Changes in Equity")
    const hasCrossSheetError = errors.some(error =>
      error.includes(SOCIE_SHEET_LABEL)
    );

    if (!hasCrossSheetError) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 hasCrossSheetError [${suffix}]: No cross-sheet errors found`);
      return false;
    }

    // Check if this specific column (suffix) has an error mentioned in the error messages
    const columnLabel = this.getColumnLabel(suffix);
    const hasErrorForThisColumn = errors.some(error => error.includes(columnLabel));

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 hasCrossSheetError [${suffix}]: columnLabel="${columnLabel}", hasErrorForThisColumn=${hasErrorForThisColumn}`);

    return hasErrorForThisColumn;
  }

  /**
   * Get user-friendly column label for a suffix
   */
  private getColumnLabel(suffix: string): string {
    const labelMap: { [key: string]: string } = {
      'ConsolidatedCurrent': 'Consolidated Current',
      'ConsolidatedPrevious': 'Consolidated Previous',
      'SeparateCurrent': 'Separate Current',
      'SeparatePrevious': 'Separate Previous'
    };
    return labelMap[suffix] || suffix;
  }

  /**
   * Validate that ComprehensiveIncome matches ComprehensiveIncome_2
   * Called before submitting for validation
   * @returns true if validation passes, false if there are mismatches
   */
  validateComprehensiveIncomeMatch(): boolean {
    if (!this.schema) return true;

    // Clear previous mismatch errors
    this.comprehensiveIncomeMismatchErrors.clear();

    // Determine if MFRS or MPERS
    const isMFRS = this.crossRefService.isMFRS();
    const comprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome';
    const comprehensiveIncomeQname2 = isMFRS
      ? 'ifrs-full:ComprehensiveIncome_2'
      : 'ifrs-smes:ComprehensiveIncome_2';

    const columnMappings: Array<{ consolidatedOrSeparate: 'Consolidated' | 'Separate'; period: 'Current' | 'Previous'; label: string }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', label: 'Consolidated Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', label: 'Consolidated Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current', label: 'Separate Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', label: 'Separate Previous' },
    ];

    let hasError = false;

    columnMappings.forEach(({ consolidatedOrSeparate, period }) => {
      const controlName1 = this.getXbrlControlName(comprehensiveIncomeQname, consolidatedOrSeparate, period);
      const controlName2 = this.getXbrlControlName(comprehensiveIncomeQname2, consolidatedOrSeparate, period);

      const control1 = this.statementOfComprehensiveIncomeForm.get(controlName1);
      const control2 = this.statementOfComprehensiveIncomeForm.get(controlName2);

      if (control1 && control2) {
        // Round to 2 decimal places to avoid floating-point precision issues
        const value1 = Math.round((parseFloat(control1.value) || 0) * 100) / 100;
        const value2 = Math.round((parseFloat(control2.value) || 0) * 100) / 100;

        // Require exact match (zero tolerance)
        if (value1 !== value2) {
          hasError = true;

          // Track these fields for red border styling
          this.comprehensiveIncomeMismatchErrors.add(controlName1);
          this.comprehensiveIncomeMismatchErrors.add(controlName2);

          // Mark both controls as invalid with custom error
          control1.setErrors({ comprehensiveIncomeMismatch: true });
          control2.setErrors({ comprehensiveIncomeMismatch: true });
          control1.markAsTouched();
          control2.markAsTouched();
          control1.markAsDirty();
          control2.markAsDirty();
        } else {
          // Clear any previous comprehensiveIncomeMismatch errors
          if (control1.hasError('comprehensiveIncomeMismatch')) {
            const errors1 = { ...control1.errors };
            delete errors1['comprehensiveIncomeMismatch'];
            control1.setErrors(Object.keys(errors1).length > 0 ? errors1 : null);
          }
          if (control2.hasError('comprehensiveIncomeMismatch')) {
            const errors2 = { ...control2.errors };
            delete errors2['comprehensiveIncomeMismatch'];
            control2.setErrors(Object.keys(errors2).length > 0 ? errors2 : null);
          }
        }
      }
    });

    return !hasError;
  }

  /**
   * Check if a specific control has comprehensive income mismatch error
   * Used for applying red border styling in template.
   *
   * TWO stores, because this sheet computes the SAME comparison twice:
   *
   *  - `runSameSheetEqualityValidations()` runs LIVE on every edit cycle and
   *    writes `"<column>: <mismatch>"` onto BOTH sides of the pair. It is what
   *    produces the row's Error Description text.
   *  - `validateComprehensiveIncomeMatch()` fills
   *    `comprehensiveIncomeMismatchErrors`, and its only caller in the repo is
   *    `[customValidation]` on `<submit-for-validation>` — so it runs at submit
   *    and at no other time.
   *
   * Reading only the second store meant `ifrs-full:ComprehensiveIncome_2` — whose
   * SOLE rule is that same-sheet equality — rendered its text with no border
   * possible: `has-required-error` cannot fire on a `calculated` row, and
   * `has-cross-sheet-error` needs a SoCIE-labelled message that
   * `validateComprehensiveIncomeAgainstRole19()` deliberately never writes for
   * the `_2` occurrence. P.O. 2026-08-26: border and message are one affordance.
   *
   * The live half is column-scoped through `getColumnLabel` (the same labels the
   * producer prefixes with) and discriminated by `isOwnSameSheetMismatch`, so a
   * CROSS-SHEET message on the same key stays the business of
   * `hasCrossSheetError` and cannot double-paint here.
   */
  hasComprehensiveIncomeMismatch(controlBaseName: string, suffix: string): boolean {
    // Convert template suffix format to bracket notation to match the Set keys
    const suffixMap: { [key: string]: string } = {
      'ConsolidatedCurrent': '[Consolidated][CurrentYear]',
      'ConsolidatedPrevious': '[Consolidated][PreviousYear]',
      'SeparateCurrent': '[Separate][CurrentYear]',
      'SeparatePrevious': '[Separate][PreviousYear]',
    };
    const bracketSuffix = suffixMap[suffix] || suffix;
    const fullControlName = controlBaseName + bracketSuffix;
    if (this.comprehensiveIncomeMismatchErrors.has(fullControlName)) return true;

    // Live same-sheet equality, scoped to THIS column.
    const columnLabel = this.getColumnLabel(suffix);
    const errors = this.validationErrors.get(controlBaseName) || [];
    return errors.some(
      (msg) => this.isOwnSameSheetMismatch(msg) && msg.startsWith(columnLabel + ':')
    );
  }

  /**
   * Auto-check and clear comprehensive income mismatch errors when values match again
   * Called automatically on form value changes
   */
  private autoCheckComprehensiveIncomeMatch(): void {
    // Only check if there are existing errors to clear
    if (this.comprehensiveIncomeMismatchErrors.size === 0) return;

    // Determine if MFRS or MPERS
    const isMFRS = this.crossRefService.isMFRS();
    const comprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome';
    const comprehensiveIncomeQname2 = isMFRS
      ? 'ifrs-full:ComprehensiveIncome_2'
      : 'ifrs-smes:ComprehensiveIncome_2';

    const columnMappings: Array<{ consolidatedOrSeparate: 'Consolidated' | 'Separate'; period: 'Current' | 'Previous' }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];

    columnMappings.forEach(({ consolidatedOrSeparate, period }) => {
      const controlName1 = this.getXbrlControlName(comprehensiveIncomeQname, consolidatedOrSeparate, period);
      const controlName2 = this.getXbrlControlName(comprehensiveIncomeQname2, consolidatedOrSeparate, period);

      const control1 = this.statementOfComprehensiveIncomeForm.get(controlName1);
      const control2 = this.statementOfComprehensiveIncomeForm.get(controlName2);

      if (control1 && control2) {
        // Round to 2 decimal places to avoid floating-point precision issues
        const value1 = Math.round((parseFloat(control1.value) || 0) * 100) / 100;
        const value2 = Math.round((parseFloat(control2.value) || 0) * 100) / 100;

        // Check if values now match exactly (zero tolerance)
        if (value1 === value2) {
          // Clear errors for these controls
          this.comprehensiveIncomeMismatchErrors.delete(controlName1);
          this.comprehensiveIncomeMismatchErrors.delete(controlName2);

          // Clear form control errors
          if (control1.hasError('comprehensiveIncomeMismatch')) {
            const errors1 = { ...control1.errors };
            delete errors1['comprehensiveIncomeMismatch'];
            control1.setErrors(Object.keys(errors1).length > 0 ? errors1 : null);
          }
          if (control2.hasError('comprehensiveIncomeMismatch')) {
            const errors2 = { ...control2.errors };
            delete errors2['comprehensiveIncomeMismatch'];
            control2.setErrors(Object.keys(errors2).length > 0 ? errors2 : null);
          }
        }
      }
    });
  }

  onCancelConfirmed(): void {
    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfComprehensiveIncome);

    // Reset validation complete flag before reloading
    this.isValidationComplete = false;

    // Reload from the reverted state service data
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.statementOfComprehensiveIncomeForm.markAsPristine();
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
